// src/DashboardView.js
import React, { useState, useRef, useEffect } from 'react';
import api, { API_BASE } from './apiClient';

import ApprovalMatrix from './ApprovalMatrix';
import Fade from '@mui/material/Fade';
import CircularProgress from '@mui/material/CircularProgress';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import GroupsIcon from '@mui/icons-material/Group';
import GavelIcon from '@mui/icons-material/Gavel';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ContactPageIcon from '@mui/icons-material/ContactPage';


import {
  descendingComparator,
  getComparator,
  stableSort,
  PRIORITIES,
  HOME_TABLE_AVAILABLE_COLUMNS,
  getDueHealth,
  getTaskTags,
  TaskTable,
  SimpleListCard,
  ProjectTasksTable,
} from './DashboardTables';

import TaskDetailPanel from './TaskDetailPanel';
import RoleManager from './RoleManager';
import ProjectRoleManager from './ProjectRoleManager';
import CustomRoleDialog from './CustomRoleDialog';
import WorkspaceInviteDialog from './WorkspaceInviteDialog';
import PermissionGrid from './PermissionGrid';
import ArchiveModal from './ArchiveModal';
import ProjectMultiTagSelector from './ProjectMultiTagSelector';
import Profile from './Profile';
import BoardView from './BoardView';
import CalendarView from './CalendarView';
import ProjectDialog from './ProjectDialog';
import ProjectCompletionRulesDialog from './ProjectCompletionRulesDialog';

import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';


import Brightness4Icon from '@mui/icons-material/Brightness4';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear';
import LogoutIcon from '@mui/icons-material/Logout';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import LabelIcon from '@mui/icons-material/Label';
import ListItemButton from '@mui/material/ListItemButton';

import {
  AppBar,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  FormControl,
  InputLabel,
  InputAdornment,
  Checkbox,
  Menu,
  FormControlLabel,
  Tabs,
  Tab,
  Toolbar,
  TextField,
  Avatar,
  Breadcrumbs,
  ListItemIcon,
  Tooltip,
} from '@mui/material';

const getUploadsBase = () => {
  if (API_BASE && API_BASE.startsWith('http')) return API_BASE;
  if (api?.defaults?.baseURL && /^https?:/i.test(api.defaults.baseURL)) return api.defaults.baseURL;
  return window.location.origin;
};

const matchesPreset = (dateVal, preset, customFrom, customTo) => {
  if (!preset || preset === 'all') return true;
  if (!dateVal) return false;

  const normalize = (val) => {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const d = normalize(dateVal);
  if (!d) return false;

  const today = normalize(new Date());

  const startOfWeek = normalize(new Date(today));
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(endOfWeek.getDate() + 6);

  const nextWeekStart = new Date(endOfWeek);
  nextWeekStart.setDate(nextWeekStart.getDate() + 1);
  const nextWeekEnd = new Date(nextWeekStart);
  nextWeekEnd.setDate(nextWeekEnd.getDate() + 6);

  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const startOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);

  const inRange = (from, to) => {
    const f = from ? normalize(from) : null;
    const t = to ? normalize(to) : null;
    if (f && d < f) return false;
    if (t && d > t) return false;
    return true;
  };

  switch (preset) {
    case 'today':
      return d.getTime() === today.getTime();
    case 'tomorrow': {
      const tmr = new Date(today);
      tmr.setDate(tmr.getDate() + 1);
      return d.getTime() === normalize(tmr).getTime();
    }
    case 'this_week':
      return d >= startOfWeek && d <= endOfWeek;
    case 'next_week':
      return d >= nextWeekStart && d <= nextWeekEnd;
    case 'this_month':
      return d >= startOfMonth && d <= endOfMonth;
    case 'next_month':
      return d >= startOfNextMonth && d <= endOfNextMonth;
    case 'custom':
      return inRange(customFrom, customTo);
    default:
      return true;
  }
};

function buildUserDisplay(user = {}) {
  const firstName =
    user.first_name || user.firstname || user.firstName || user.name?.split?.(' ')[0] || '';
  const lastName = user.last_name || user.lastname || user.lastName || '';
  const username = user.username || user.email || '';
  const fullName = `${(firstName || '').trim()} ${(lastName || '').trim()}`.trim() || username || 'User';

  const words = (firstName || '').trim().split(/\s+/).filter(Boolean);
  let initials = '';
  if (firstName && lastName) {
    initials = `${firstName[0]}${lastName[0]}`;
  } else if (words.length >= 2) {
    initials = `${words[0][0]}${words[1][0]}`;
  } else if (words.length === 1) {
    initials = words[0][0];
  } else if (username) {
    initials = username[0];
  }
  initials = initials.toUpperCase();

  const avatarSrc =
    user.avatar ||
    user.avatar_url ||
    user.profile_pic ||
    user.profilePic ||
    user.photo_url ||
    (user.photo ? `${getUploadsBase()}/uploads/${user.photo}` : null) ||
    null;

  return { firstName: firstName || username || 'User', lastName, fullName, initials, avatarSrc };
}

const avatarPalette = ['#00897B', '#2563EB', '#7C3AED', '#EA580C', '#0EA5E9', '#E11D48', '#0F766E'];
const pickAvatarColor = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return avatarPalette[hash % avatarPalette.length];
};

const UserAvatar = ({ user, size = 28, showName = false }) => {
  const data = buildUserDisplay(user || {});
  const bg = pickAvatarColor(data.fullName || data.initials || 'u');
  return (
    <Tooltip title={data.fullName} placement="top">
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: showName ? 0.75 : 0 }}>
        <Avatar
          src={data.avatarSrc || undefined}
          sx={{ width: size, height: size, fontSize: size * 0.45, bgcolor: bg }}
        >
          {!data.avatarSrc && data.initials}
        </Avatar>
        {showName && (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {data.firstName}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

// Project avatar helper (workspace-scoped color + cleaner initials)
const projectPalette = ['#0EA5E9', '#7C3AED', '#F59E0B', '#10B981', '#EC4899', '#6366F1', '#22C55E'];
const pickProjectColor = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 33 + seed.charCodeAt(i)) >>> 0;
  return projectPalette[hash % projectPalette.length];
};

const getProjectInitials = (name = '') => {
  const clean = name.replace(/[^A-Za-z0-9\s]/g, ' ');
  const parts = clean.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'P';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const ProjectAvatar = ({ name = 'Project', workspaceId = '', size = 28 }) => {
  const initials = getProjectInitials(name);
  const bg = pickProjectColor(`${workspaceId}:${name || initials}`);
  return (
    <Avatar sx={{ width: size, height: size, fontSize: size * 0.45, bgcolor: bg }}>
      {initials}
    </Avatar>
  );
};

function DashboardView({
  currentView,
  sidebarAutoCollapse,
  setSidebarAutoCollapse,
  sidebarHovering,
  setSidebarHovering,
  greeting,
  selectedWorkspaceObj,
  selectedProjectObj,
  viewMode,
  setViewMode,
  showArchivedTasks,
  setShowArchivedTasks,
  taskStatusFilter,
  setTaskStatusFilter,
  taskPriorityFilter,
  setTaskPriorityFilter,
  taskAssigneeFilter,
  setTaskAssigneeFilter,
  taskTagFilter,
  setTaskTagFilter,
  taskDuePreset,
  setTaskDuePreset,
  taskTargetPreset,
  setTaskTargetPreset,
  taskCreatorFilter,
  setTaskCreatorFilter,
  taskDueCustomFrom,
  taskDueCustomTo,
  setTaskDueCustomFrom,
  setTaskDueCustomTo,
  taskTargetCustomFrom,
  taskTargetCustomTo,
  setTaskTargetCustomFrom,
  setTaskTargetCustomTo,
  taskQuickSearch,
  setTaskQuickSearch,
  homeTableColumns,
  setHomeTableColumns,
  loadingTasks,
  topBarUserName,
  selectedWorkspace,
  selectedProject,
  projects,
  tasks,
  assignedTasks,
  workspaces,
  workspaceUsers,
  projectUsers,
  goals,
  handleOpenProfileMenu,
  profileMenuAnchor,
  handleCloseProfileMenu,
  setProfileOpen,
  setPermissionGridOpen,
  setArchiveOpen,
  setProjectDialogOpen,
  setCustomRoleDialogOpen,
  setRoleManagerOpen,
  setProjectRoleManagerOpen,
  setInviteDialogOpen,
  handleModify,
  openTaskPanel,
  getUserName,
  handleApproveWorkLog,
  workDoneViewMode,
  workDoneRows,
  workDoneSummary,
  workDoneRangeKey,
  setWorkDoneRangeKey,
  workDoneCustomFrom,
  setWorkDoneCustomFrom,
  workDoneCustomTo,
  setWorkDoneCustomTo,
  workDoneStatusFilter,
  setWorkDoneStatusFilter,
  workDoneProjectFilterIds,
  setWorkDoneProjectFilterIds,
  selectedTasks,
  setSelectedTasks,
  bulkDialogOpen,
  setBulkDialogOpen,
  bulkForm,
  setBulkForm,
  loadingWorkspaces,
  loadingProjects,
  loadingWorkspaceUsers,
  loadingProjectUsers,
  enqueueSnackbar,
  projectDialogOpen,
  projectName,
  setProjectName,
  handleCreateProject,
  taskDialogOpen,
  setTaskDialogOpen,
  taskForm,
  setTaskForm,
  linkedProjectIds,
  setLinkedProjectIds,
  handleCreateTask,
  taskFormFiles,
  setTaskFormFiles,
  handleCloseTaskDialog,
  taskPanelOpen,
  setTaskPanelOpen,
  selectedTaskForPanel,
  setSelectedTaskForPanel,
  selectedTaskComments,
  setSelectedTaskComments,
  taskCommentDraft,
  setTaskCommentDraft,
  taskSteps,
  setTaskSteps,
  availableStepGroups,
  setAvailableStepGroups,
  selectedStepGroupForAttach,
  setSelectedStepGroupForAttach,
  taskWorkLogs,
  setTaskWorkLogs,
  taskActivityLogs,
  setTaskActivityLogs,
  taskDetailTab,
  setTaskDetailTab,
  activityFrom,
  setActivityFrom,
  activityTo,
  setActivityTo,
  activityUserFilter,
  setActivityUserFilter,
  taskWorkLogDraft,
  setTaskWorkLogDraft,
  availableWorkLogApprovers,
  setAvailableWorkLogApprovers,
  userId,
  handleSaveTaskDetails,
  handleArchiveTask,
  handleAddTaskComment,
  handleCollaboratorsChange,
  loadTaskComments,
  handleToggleStep,
  handleAttachStepGroup,
  handleAddWorkLog,
  loadTask,
  loadTaskActivityLogs,
  handleSaveProjectNotes,
  projectSaving,
  fetchTasks,
  taskDetailSaving,
  canLogWorkOnSelectedTask,
  loadTaskWorkLogs,
  setTaskDetailSaving,
  roleManagerOpen,
  projectRoleManagerOpen,
  inviteDialogOpen,
  permissionGridOpen,
  customRoles,
  archiveOpen,
  editingProject,
  customRoleDialogOpen,
  profileOpen,
  workDoneSelectedDate,
  setSelectedWorkspace,
  setSelectedProject,
  setCurrentView,
  onToggleMode,
  setLogoutDialogOpen,
  logoutDialogOpen,
  onLogout,
  pendingInvites,
  handleInviteAction,
  setShowApprovalMatrix,
  showApprovalMatrix,
  setWorkDoneViewMode,
  setWorkDoneSelectedDate,
  workspaceTab,
  setWorkspaceTab,
  setWorkspaceLogs,
  workspaceLogs,
  fetchWorkspaces,
  projectNotesDraft,
  setProjectNotesDraft,
  setEditingProject,
  setTasks,
  fetchProjects,
  handleWorkspaceChange,
}) {
const currentUserName = topBarUserName || '';
const [searchText, setSearchText] = React.useState('');
const [taskDateFilter, setTaskDateFilter] = useState(null); // { field, start, end }
const [profileDetails, setProfileDetails] = useState(null);
const [cachedPhotoUrl, setCachedPhotoUrl] = useState(() => {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem('user_photo_url') || '';
});

  // Try to find the full user record from workspace / project users
  // Try to find the full user record from workspace / project users
const currentUser =
  (workspaceUsers || []).find(
    (u) =>
      u.username === currentUserName ||           // match by username
      Number(u.id) === Number(userId) ||          // or numeric id
      Number(u.user_id) === Number(userId)        // or user_id if that is used
  ) ||
  (projectUsers || []).find(
    (u) =>
      u.username === currentUserName ||
      Number(u.id) === Number(userId) ||
      Number(u.user_id) === Number(userId)
  ) ||
  { id: userId, username: currentUserName || `User ${userId}` };

// Load full profile when profile menu opens
useEffect(() => {
  const loadProfile = async () => {
    if (!profileMenuAnchor || !userId) return;
    try {
      const res = await api.get('/api/profile', { params: { user_id: userId } });
      setProfileDetails(res.data || null);
      if (res.data?.photo_url) {
        setCachedPhotoUrl(res.data.photo_url);
        localStorage.setItem('user_photo_url', res.data.photo_url);
      }
    } catch (err) {
      console.error('Failed to load profile for menu', err?.response?.data || err.message);
    }
  };
  loadProfile();
}, [profileMenuAnchor, userId]);

// Load profile once on mount/user change to keep avatar consistent
useEffect(() => {
  let active = true;
  const loadProfile = async () => {
    if (!userId) return;
    try {
      const res = await api.get('/api/profile', { params: { user_id: userId } });
      if (active) setProfileDetails(res.data || null);
      if (active && res.data?.photo_url) {
        setCachedPhotoUrl(res.data.photo_url);
        localStorage.setItem('user_photo_url', res.data.photo_url);
      }
    } catch (err) {
      console.error('Failed to prefetch profile', err?.response?.data || err.message);
    }
  };
  loadProfile();
  return () => {
    active = false;
  };
}, [userId]);

const displayUser = profileDetails ? { ...currentUser, ...profileDetails } : currentUser;
// inject cached photo when available
const displayUserWithPhoto = {
  ...displayUser,
  photo_url: displayUser?.photo_url || cachedPhotoUrl || displayUser?.photo_url,
};


  // Read first / last name / username / email from the record
  // Adjust field names here if your API uses different ones
  const profileFirstName =
    displayUser?.first_name ||
    displayUser?.firstname ||
    displayUser?.firstName ||
    (currentUserName || '').split(' ')[0] ||
    '';

  const profileLastName =
    displayUser?.last_name ||
    displayUser?.lastname ||
    displayUser?.lastName ||
    '';

  const profileUserName =
    displayUser?.username ||
    currentUserName ||
    '';

  const profileEmail =
    displayUser?.email ||
    displayUser?.email_id ||
    displayUser?.emailId ||
    displayUser?.mail ||
    displayUser?.user_email ||
    (currentUserName && currentUserName.includes('@') ? currentUserName : '') ||
    '';


const getTimeOfDay = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};
const currentUserDisplay = buildUserDisplay(currentUser || { username: currentUserName });
  const [projectSettingsAnchor, setProjectSettingsAnchor] = useState(null);
  const projectSettingsOpen = Boolean(projectSettingsAnchor);

  const [completionRulesDialogOpen, setCompletionRulesDialogOpen] = useState(false);
  const emptySearchResults = {
    projects: [],
    tasks: [],
    comments: [],
    work_logs: [],
    attachments: [],
    tags: [],
  };
  const [searchResults, setSearchResults] = useState(emptySearchResults);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchMessage, setSearchMessage] = useState('');
  const searchAnchorRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const [isFirstVisitToday, setIsFirstVisitToday] = useState(true);
  const [savedTaskViews, setSavedTaskViews] = useState([]);
  const [activeTaskViewId, setActiveTaskViewId] = useState(null);
  const [savedViewLoading, setSavedViewLoading] = useState(false);

  useEffect(() => {
    const todayKey = new Date().toISOString().slice(0, 10);
    const lastKey = localStorage.getItem('lastGreetingDate');
    if (lastKey === todayKey) {
      setIsFirstVisitToday(false);
    } else {
      setIsFirstVisitToday(true);
      localStorage.setItem('lastGreetingDate', todayKey);
    }
  }, []);
  const getTaskById = (taskId) => {
    const tid = Number(taskId);
    return (
      (tasks || []).find((t) => Number(t.id) === tid) ||
      (assignedTasks || []).find((t) => Number(t.id) === tid)
    );
  };

  const goToProjectTasks = (projId, wsId) => {
    if (wsId) setSelectedWorkspace(String(wsId));
    if (projId) setSelectedProject(String(projId));
    setCurrentView('project-tasks');
    setViewMode('list');
  };

  const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const highlightMatch = (text, query) => {
    if (!text || !query) return text;
    const parts = String(text).split(new RegExp(`(${escapeRegex(query)})`, 'ig'));
    return parts.map((part, idx) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={idx} style={{ background: 'rgba(14,165,233,0.18)', padding: '0 2px' }}>
          {part}
        </mark>
      ) : (
        <React.Fragment key={idx}>{part}</React.Fragment>
      )
    );
  };

  const openTaskFromSearch = async (taskId) => {
    const existing = getTaskById(taskId);
    if (existing) {
      openTaskPanel(existing);
      return;
    }
    try {
      await fetchTasks();
      const refreshed = getTaskById(taskId);
      if (refreshed) {
        openTaskPanel(refreshed);
      } else {
        enqueueSnackbar?.('Task not found or not accessible', { variant: 'warning' });
      }
    } catch (err) {
      console.error('Fetch task for search failed:', err?.response?.data || err.message);
      enqueueSnackbar?.('Unable to open task', { variant: 'error' });
    }
  };


  const isSidebarExpanded = !sidebarAutoCollapse || sidebarHovering;
  const isSidebarCollapsed = sidebarAutoCollapse && !sidebarHovering;
  const showSidebarLabels = !isSidebarCollapsed;
  const sidebarWidth = isSidebarExpanded ? 240 : 60;

  const handleWorkspaceSwitch = (newWorkspaceId) => {
    const idStr = String(newWorkspaceId || '');
    setSelectedWorkspace(idStr);
    setSelectedProject('');
    setCurrentView('workspace-overview');

    if (idStr) {
      fetchProjects(idStr);
    }
  };

  const handleSaveCompletionRules = async (payload) => {
    if (!selectedProject) return;

    try {
      await api.put(`/api/projects/${selectedProject}`, {
        ...payload,
        user_id: userId,
      });

      enqueueSnackbar('Task completion rules updated', {
        variant: 'success',
      });

      setCompletionRulesDialogOpen(false);

      if (selectedWorkspace) {
        fetchProjects(selectedWorkspace); // refresh projects with new rules
      }
    } catch (err) {
      console.error(
        'Error saving completion rules',
        err.response?.data || err.message
      );
      enqueueSnackbar('Failed to save completion rules', {
        variant: 'error',
      });
    }
  };


  

  const handleGlobalSearchSubmit = () => {
    if (!searchText.trim()) return;
    triggerSearch(searchText, true);
  };

  const triggerSearch = (val, immediate = false) => {
    setSearchText(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    const query = (val || '').trim();
    if (!query || query.length < 2) {
      setSearchOpen(false);
      setSearchLoading(false);
      setSearchResults(emptySearchResults);
      setSearchMessage(query.length ? 'Keep typing (2+ characters)...' : '');
      return;
    }

    const runSearch = async () => {
      setSearchLoading(true);
      try {
        const { data } = await api.get('/api/search', {
          params: {
            q: query,
            user_id: userId,
            workspace_id: selectedWorkspace || undefined,
            project_id: selectedProject || undefined,
          },
        });
        const next = data || emptySearchResults;
        setSearchResults(next);
        const hasResults = Object.values(next || {}).some(
          (arr) => Array.isArray(arr) && arr.length
        );
        setSearchMessage(hasResults ? '' : 'No matches found');
        setSearchOpen(true);
      } catch (err) {
        console.error('Search error:', err?.response?.data || err.message);
        setSearchMessage('Search failed. Please try again.');
        setSearchOpen(true);
      } finally {
        setSearchLoading(false);
      }
    };

    if (immediate) {
      runSearch();
    } else {
      searchDebounceRef.current = setTimeout(runSearch, 250);
    }
  };

  const handleSearchChange = (val) => {
    triggerSearch(val, false);
  };

  const handleApproveCompletion = async (task) => {
    if (!task?.id) return;
    try {
      await api.put(`/api/tasks/${task.id}`, {
        status: 'closed',
        archived: true,
        user_id: userId,
      });
      enqueueSnackbar('Task completion approved', { variant: 'success' });
      fetchTasks();
    } catch (err) {
      console.error('Approve completion failed:', err?.response?.data || err.message);
      enqueueSnackbar(
        err?.response?.data?.error || 'Failed to approve task completion',
        { variant: 'error' }
      );
    }
  };

  const hasSearchResults = Object.values(searchResults || {}).some(
    (arr) => Array.isArray(arr) && arr.length
  );

  const handleViewAllGroup = (groupKey) => {
    setSearchOpen(false);
    if (groupKey === 'tasks') {
      const firstTask = (searchResults.tasks || [])[0];
      const wsId = firstTask?.workplace_id || selectedWorkspace;
      const projId = firstTask?.project_id || selectedProject;
      if (wsId && wsId !== selectedWorkspace) {
        fetchProjects(String(wsId));
        setSelectedWorkspace(String(wsId));
      }
      goToProjectTasks(projId || '', wsId || '');
      return;
    }
    if (groupKey === 'projects') {
      const firstProj = (searchResults.projects || [])[0];
      const wsId = firstProj?.workplace_id || selectedWorkspace;
      if (wsId && wsId !== selectedWorkspace) {
        fetchProjects(String(wsId));
        setSelectedWorkspace(String(wsId));
      }
      goToProjectTasks(firstProj?.id || selectedProject || '', wsId || '');
    }
  };

  const applyCalendarFilterToList = (payload = {}) => {
    const {
      dateField = 'due_date',
      dateRange,
      date,
      filterType,
      filterValue,
    } = payload;

    setCurrentView('project-tasks');
    setViewMode('list');

    if (filterType === 'status') {
      setTaskStatusFilter(filterValue || 'all');
      setTaskAssigneeFilter('all');
      setTaskPriorityFilter('all');
    } else if (filterType === 'assignee') {
      setTaskAssigneeFilter(filterValue || 'all');
      setTaskStatusFilter('all');
      setTaskPriorityFilter('all');
    } else if (filterType === 'priority') {
      setTaskPriorityFilter(filterValue || 'all');
      setTaskStatusFilter('all');
      setTaskAssigneeFilter('all');
    }
    setTaskTagFilter('all');

    const range = dateRange || (date ? { start: date, end: date } : null);
    if (range?.start && range?.end) {
      setTaskDateFilter({
        field: dateField,
        start: range.start,
        end: range.end,
      });
    } else {
      setTaskDateFilter(null);
    }
  };

  // ---------- Saved task views ----------
  const buildCurrentTaskFilters = () => ({
    status: taskStatusFilter,
    priority: taskPriorityFilter,
    assignee: taskAssigneeFilter,
    tag: taskTagFilter,
    duePreset: taskDuePreset,
    targetPreset: taskTargetPreset,
    creator: taskCreatorFilter,
    dueFrom: taskDueCustomFrom,
    dueTo: taskDueCustomTo,
    targetFrom: taskTargetCustomFrom,
    targetTo: taskTargetCustomTo,
    showArchived: showArchivedTasks,
  });

  const applyTaskViewFilters = (filters = {}) => {
    setTaskStatusFilter(filters.status || 'all');
    setTaskPriorityFilter(filters.priority || 'all');
    setTaskAssigneeFilter(filters.assignee || 'all');
    setTaskTagFilter(filters.tag || 'all');
    setTaskDuePreset(filters.duePreset || 'all');
    setTaskTargetPreset(filters.targetPreset || 'all');
    setTaskCreatorFilter(filters.creator || 'all');
    setTaskDueCustomFrom(filters.dueFrom || '');
    setTaskDueCustomTo(filters.dueTo || '');
    setTaskTargetCustomFrom(filters.targetFrom || '');
    setTaskTargetCustomTo(filters.targetTo || '');
    setShowArchivedTasks(!!filters.showArchived);
  };

  const loadSavedTaskViews = async () => {
    if (!userId || currentView !== 'project-tasks') {
      setSavedTaskViews([]);
      setActiveTaskViewId(null);
      return;
    }
    setSavedViewLoading(true);
    try {
      const params = { user_id: userId };
      if (selectedProject) params.project_id = selectedProject;
      else if (selectedWorkspace) params.workspace_id = selectedWorkspace;
      const { data } = await api.get('/api/task-views', { params });
      const views = data || [];
      setSavedTaskViews(views);
      const def = views.find((v) => v.is_default);
      if (def) {
        setActiveTaskViewId(def.id);
        applyTaskViewFilters(def.filters || {});
      }
    } catch (err) {
      console.error('Load task views error:', err?.response?.data || err.message);
      enqueueSnackbar?.('Failed to load saved views', { variant: 'error' });
    } finally {
      setSavedViewLoading(false);
    }
  };

  useEffect(() => {
    loadSavedTaskViews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, selectedProject, selectedWorkspace, currentView]);

  const handleApplyTaskView = (viewId) => {
    if (!viewId) {
      setActiveTaskViewId(null);
      return;
    }
    const view = savedTaskViews.find((v) => Number(v.id) === Number(viewId));
    if (!view) return;
    setActiveTaskViewId(view.id);
    applyTaskViewFilters(view.filters || {});
  };

  const handleSaveTaskView = async ({ update = false } = {}) => {
    if (!userId) return;
    const existing = savedTaskViews.find((v) => Number(v.id) === Number(activeTaskViewId));
    const defaultName = update ? existing?.name || '' : '';
    const name = window.prompt(
      update ? 'Update saved view name' : 'Save current filters as',
      defaultName
    );
    if (!name) return;

    const payload = {
      user_id: userId,
      workspace_id: selectedWorkspace || null,
      project_id: selectedProject || null,
      name,
      filters: buildCurrentTaskFilters(),
    };

    try {
      setSavedViewLoading(true);
      let saved;
      if (update && existing) {
        const { data } = await api.put(`/api/task-views/${existing.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post('/api/task-views', payload);
        saved = data;
      }
      await loadSavedTaskViews();
      if (saved?.id) setActiveTaskViewId(saved.id);
      enqueueSnackbar?.('Saved view updated', { variant: 'success' });
    } catch (err) {
      console.error('Save task view error:', err?.response?.data || err.message);
      enqueueSnackbar?.('Failed to save view', { variant: 'error' });
    } finally {
      setSavedViewLoading(false);
    }
  };

  const handleDeleteTaskView = async () => {
    if (!activeTaskViewId || !userId) return;
    const confirmed = window.confirm('Delete this saved view?');
    if (!confirmed) return;
    try {
      setSavedViewLoading(true);
      await api.delete(`/api/task-views/${activeTaskViewId}`, {
        params: { user_id: userId },
      });
      setActiveTaskViewId(null);
      await loadSavedTaskViews();
      enqueueSnackbar?.('Saved view deleted', { variant: 'success' });
    } catch (err) {
      console.error('Delete task view error:', err?.response?.data || err.message);
      enqueueSnackbar?.('Failed to delete view', { variant: 'error' });
    } finally {
      setSavedViewLoading(false);
    }
  };

  const currentUserAvatarUrl = null;
  
  const isHome = currentView === 'home';
  const isWorkspaceOverview = currentView === 'workspace-overview';
  const isProjectOverview = currentView === 'project-overview';
  const isProjectTasks = currentView === 'project-tasks';
  const isPersonalWorkspace = selectedWorkspaceObj?.name === 'Personal';
  const isPersonalTasksView = currentView === 'personal-tasks';
  const isAnyTasksView = isProjectTasks || isPersonalTasksView;
  const projectHeaderMb = isAnyTasksView ? 0.75 : 2;   // reduce gap only on Tasks page
  const mainGridSpacing = isAnyTasksView ? 2 : 3;

  const workspaceName = selectedWorkspaceObj?.name || 'Workspace';

  // Redirect legacy recurring views to tasks/overview now that recurring is removed
  useEffect(() => {
    if (
      currentView === 'project-recurring' ||
      currentView === 'personal-recurring' ||
      currentView === 'recurring-admin'
    ) {
      if (isPersonalWorkspace) {
        setCurrentView('personal-tasks');
      } else if (selectedProject) {
        setCurrentView('project-tasks');
      } else if (selectedWorkspace) {
        setCurrentView('workspace-overview');
      } else {
        setCurrentView('home');
      }
    }
  }, [currentView, isPersonalWorkspace, selectedProject, selectedWorkspace, setCurrentView]);

  // ---------- Breadcrumb / "URL thing" ----------
  let breadcrumbParts = []; // root

  if (isHome) {
    // just "Dashboard"
  } else if (currentView === 'workdone') {
    breadcrumbParts.push('Work Done');
  } else if (isWorkspaceOverview && selectedWorkspaceObj) {
    breadcrumbParts.push(workspaceName, 'Overview');
  } else if (isPersonalTasksView && selectedWorkspaceObj) {
    breadcrumbParts.push(workspaceName, 'Tasks');
  } else if (isProjectOverview && selectedWorkspaceObj && selectedProjectObj) {
    breadcrumbParts.push(workspaceName, selectedProjectObj.name, 'Overview');
  } else if (isProjectTasks && selectedWorkspaceObj && selectedProjectObj) {
    breadcrumbParts.push(workspaceName, selectedProjectObj.name, 'Tasks');
  }

  const currentWorkspaceName = 'Workspace';
  const breadcrumbTrail = breadcrumbParts.slice(1);

  const [workspaceMenuAnchor, setWorkspaceMenuAnchor] = useState(null);
  const [projectSearch, setProjectSearch] = useState('');

  const handleOpenWorkspaceMenu = (event) => {
    setWorkspaceMenuAnchor(event.currentTarget);
  };

  const handleCloseWorkspaceMenu = () => {
    setWorkspaceMenuAnchor(null);
  };

  const handleWorkspaceSelectFromTopBar = (workspaceId) => {
    handleCloseWorkspaceMenu();
    handleWorkspaceSwitch(workspaceId);
  };

  const isWorkspaceOwner =
    Number(selectedWorkspaceObj?.owner_id) === Number(userId);
  const isProjectOwner =
    Number(selectedProjectObj?.owner_id) === Number(userId);
  const canApproveCompletions = isProjectOwner || isWorkspaceOwner;

    // Common handler for creating a new task in the current context
  const handleNewTaskClick = () => {
    const projectIdToUse = selectedProject || taskForm.projectId || '';

    // For non-personal workspaces, force a project
    if (!projectIdToUse && !isPersonalWorkspace) {
      enqueueSnackbar('Select workspace and project', {
        variant: 'warning',
      });
      return;
    }

    setTaskForm((prev) => ({
      ...prev,
      projectId: projectIdToUse,
    }));

    setLinkedProjectIds([]);
    setTaskDialogOpen(true);
  };


  const assignableUsers = selectedProject ? projectUsers || [] : workspaceUsers || [];

  const canCreateTaskInCurrentContext =
    isHome || isPersonalWorkspace || !!selectedProject;

  const taskViewMode = viewMode || 'list';

  const projectIdsInCurrentWorkspace = new Set(
    (projects || []).map((p) => String(p.id))
  );

  const visibleProjects = selectedWorkspace
    ? (projects || []).filter(
        (p) => String(p.workplace_id) === String(selectedWorkspace)
      )
    : projects || [];
  
  const filteredProjects = (visibleProjects || []).filter((p) =>
    (p.name || '').toLowerCase().includes(projectSearch.toLowerCase())
  );

  const teamListTitle =
    isWorkspaceOwner || !selectedProject ? 'Workspace Team' : 'Project Team';
  const teamList =
    isWorkspaceOwner || !selectedProject ? workspaceUsers || [] : projectUsers || [];

  const createdByMeOpen = (tasks || []).filter(
    (t) => t.status !== 'closed' && !t.project_id
  );
  const assignedToMeOpen = (assignedTasks || []).filter(
    (t) => t.status !== 'closed' && !t.project_id
  );
  const overdueAssignedToMe = (assignedTasks || []).filter((t) => {
    const info = getDueHealth(t.due_date, t.status);
    return info.label.startsWith('Overdue');
  });
  const dueTodayAssignedToMe = (assignedTasks || []).filter((t) => {
    const info = getDueHealth(t.due_date, t.status);
    return info.label === 'Due today';
  });

  const matchesDateFilter = (value) => {
    if (!taskDateFilter || !value) return true;
    const { field, start, end } = taskDateFilter;
    if (!field) return true;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return false;
    const s = start ? new Date(start) : null;
    const e = end ? new Date(end) : null;
    if (s && d < s) return false;
    if (e && d > e) return false;
    return true;
  };

  const baseProjectTasks = showArchivedTasks
    ? tasks || []
    : (tasks || []).filter((t) => !t.archived);
  const allProjectTags = [
    ...new Set(baseProjectTasks.flatMap((t) => getTaskTags(t))),
  ];
  const filteredProjectTasks = baseProjectTasks.filter((t) => {
    let pass = true;
    if (taskStatusFilter === 'pending-approval') {
      pass = pass && t.status === 'completed' && !t.archived;
    } else if (taskStatusFilter !== 'all') {
      pass = pass && t.status === taskStatusFilter;
    }
    if (taskPriorityFilter !== 'all')
      pass = pass && t.priority === taskPriorityFilter;
    if (taskAssigneeFilter !== 'all') {
      if (taskAssigneeFilter === 'unassigned') pass = pass && !t.assigned_to;
      else pass = pass && Number(t.assigned_to) === Number(taskAssigneeFilter);
    }
    if (taskTagFilter !== 'all') {
      const tags = getTaskTags(t);
      pass = pass && tags.includes(taskTagFilter);
    }
    if (taskDateFilter?.field) {
      const dateVal =
        taskDateFilter.field === 'target_date' ? t.target_date : t.due_date;
      pass = pass && matchesDateFilter(dateVal);
    }
    if (taskQuickSearch) {
      const q = taskQuickSearch.toLowerCase();
      const title = (t.title || '').toLowerCase();
      const desc = (t.description || '').toLowerCase();
      const tags = getTaskTags(t).join(',').toLowerCase();
      pass = pass && (title.includes(q) || desc.includes(q) || tags.includes(q));
    }
    // Preset filters
    pass =
      pass &&
      matchesPreset(
        t.due_date,
        taskDuePreset,
        taskDueCustomFrom,
        taskDueCustomTo
      ) &&
      matchesPreset(
        t.target_date,
        taskTargetPreset,
        taskTargetCustomFrom,
        taskTargetCustomTo
      );
    // Creator filter
    if (taskCreatorFilter !== 'all') {
      pass = pass && Number(t.user_id) === Number(taskCreatorFilter);
    }
    return pass;
  });

  const basePersonalTasksRaw = showArchivedTasks
    ? [...(tasks || []), ...(assignedTasks || [])]
    : [...(tasks || []), ...(assignedTasks || [])].filter((t) => !t.archived);
  const basePersonalTasks = [
    ...new Set(basePersonalTasksRaw.map((t) => t.id)),
  ].map((id) => basePersonalTasksRaw.find((t) => t.id === id));
  const allPersonalTags = [
    ...new Set(basePersonalTasks.flatMap((t) => getTaskTags(t))),
  ];


  
  const filteredPersonalTasks = basePersonalTasks.filter((t) => {
    let pass = true;
    if (taskStatusFilter === 'pending-approval') {
      pass = pass && t.status === 'completed' && !t.archived;
    } else if (taskStatusFilter !== 'all') {
      pass = pass && t.status === taskStatusFilter;
    }
    if (taskPriorityFilter !== 'all')
      pass = pass && t.priority === taskPriorityFilter;
    if (taskAssigneeFilter !== 'all') {
      if (taskAssigneeFilter === 'unassigned') pass = pass && !t.assigned_to;
      else pass = pass && Number(t.assigned_to) === Number(taskAssigneeFilter);
    }
    if (taskTagFilter !== 'all') {
      const tags = getTaskTags(t);
      pass = pass && tags.includes(taskTagFilter);
    }
    if (taskDateFilter?.field) {
      const dateVal =
        taskDateFilter.field === 'target_date' ? t.target_date : t.due_date;
      pass = pass && matchesDateFilter(dateVal);
    }
    pass =
      pass &&
      matchesPreset(
        t.due_date,
        taskDuePreset,
        taskDueCustomFrom,
        taskDueCustomTo
      ) &&
      matchesPreset(
        t.target_date,
        taskTargetPreset,
        taskTargetCustomFrom,
        taskTargetCustomTo
      );
    if (taskCreatorFilter !== 'all') {
      pass = pass && Number(t.user_id) === Number(taskCreatorFilter);
    }
    return pass;
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      {/* TOP APP BAR */}
      <AppBar
              position="fixed"
              elevation={0}
              sx={(theme) => ({
                backgroundColor: '#C7FFF2',
                color: theme.palette.text.primary,
                borderBottom: '2px solid rgba(0,0,0,0.06)',
                zIndex: theme.zIndex.drawer + 1,
              })}
            >
              <Toolbar
                  sx={{
                  minHeight: 56,
                  px: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {/* LEFT: Home + breadcrumb URL */}
                <IconButton
                  edge="start"
                  size="small"
                  sx={{ mr: 0.5 }}
                  onClick={() => {
                    setSelectedWorkspace('');
                    setSelectedProject('');
                    setCurrentView('home');
                  }}
                >
                  <HomeIcon fontSize="small" />
                </IconButton>
      
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {/* App name – small */}
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, lineHeight: 1.1 }}
                  >
                    Krama
                  </Typography>
      
                  {/* Breadcrumbs – small caption */}
                  <Breadcrumbs
                    separator="/"
                    aria-label="breadcrumb"
                    sx={{
                      fontSize: 11,
                      '& .MuiBreadcrumbs-separator': { mx: 0.5 },
                    }}
                  >
                    {breadcrumbParts.map((part, idx) => (
                      <Typography
                        key={idx}
                        variant="caption"
                        color={
                          idx === breadcrumbParts.length - 1
                            ? 'text.primary'
                            : 'text.secondary'
                        }
                        sx={{
                          fontWeight: idx === breadcrumbParts.length - 1 ? 600 : 400,
                        }}
                      >
                        {part}
                      </Typography>
                    ))}
                  </Breadcrumbs>
                </Box>
      
      
                {/* CENTER: Search bar */}
                <Box
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Box sx={{ position: 'relative', width: '100%', maxWidth: 520 }} ref={searchAnchorRef}>
                    <TextField
                      placeholder="Search projects, tasks, comments, work logs..."
                      size="small"
                      value={searchText}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setSearchOpen(false);
                          e.currentTarget.blur();
                        }
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          triggerSearch(searchText, true);
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: searchText ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSearchText('');
                                setSearchResults(emptySearchResults);
                                setSearchMessage('');
                                setSearchOpen(false);
                              }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                      sx={{
                        width: '100%',
                        bgcolor: 'common.white',
                        borderRadius: 999,
                        '& .MuiOutlinedInput-root': {
                          height: 36,
                          paddingRight: 1,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0,0,0,0.06)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0,0,0,0.12)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      }}
                    />
                    {searchOpen && (
                      <Paper
                        elevation={6}
                        sx={{
                          position: 'absolute',
                          top: 44,
                          left: 0,
                          right: 0,
                          maxHeight: 360,
                          overflowY: 'auto',
                          borderRadius: 2,
                          zIndex: 2000,
                        }}
                      >
                        {searchLoading && (
                          <Typography variant="body2" sx={{ p: 1.5 }}>
                            Searching...
                          </Typography>
                        )}
                        {!searchLoading && (
                          <Box>
                            {searchMessage && !hasSearchResults && (
                              <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                                {searchMessage}
                              </Typography>
                            )}
                            {[
                              { key: 'projects', label: 'Projects', icon: <FolderIcon fontSize="small" />, color: '#0ea5e9' },
                              { key: 'tasks', label: 'Tasks', icon: <CheckCircleIcon fontSize="small" />, color: '#22c55e' },
                              { key: 'comments', label: 'Comments', icon: <ChatBubbleOutlineIcon fontSize="small" />, color: '#6366f1' },
                              { key: 'work_logs', label: 'Work Logs', icon: <AccessTimeIcon fontSize="small" />, color: '#f59e0b' },
                              { key: 'attachments', label: 'Attachments', icon: <AttachFileIcon fontSize="small" />, color: '#fb7185' },
                              { key: 'tags', label: 'Tags', icon: <LabelIcon fontSize="small" />, color: '#0ea5e9' },
                            ].map((group) => {
                              const items = searchResults[group.key] || [];
                              if (!items.length) return null;
                              return (
                                <Box key={group.key} sx={{ borderBottom: '1px solid', borderColor: 'divider', p: 1.2 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 700,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      mb: 0.5,
                                      color: group.color,
                                    }}
                                  >
                                    {group.icon} {group.label}
                                  </Typography>
                                  {items.slice(0, 5).map((item) => (
                                    <Box
                                      key={`${group.key}-${item.id || item.filename || item}`}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        py: 0.5,
                                        px: 0.5,
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: 'rgba(15,118,110,0.08)' },
                                      }}
                                      onClick={() => {
                                        setSearchOpen(false);
                                        if (group.key === 'tasks') {
                                          if (item.project_id) {
                                            goToProjectTasks(item.project_id, item.workplace_id);
                                          }
                                          openTaskFromSearch(item.id);
                                        }
                                        if (group.key === 'projects') {
                                          goToProjectTasks(item.id, item.workplace_id);
                                        }
                                        if (group.key === 'comments') {
                                          goToProjectTasks(item.project_id || item.task_project_id, item.workplace_id);
                                          openTaskFromSearch(item.task_id);
                                          setTaskDetailTab('comments');
                                        }
                                        if (group.key === 'work_logs') {
                                          goToProjectTasks(item.project_id || item.task_project_id, item.workplace_id);
                                          openTaskFromSearch(item.task_id);
                                          setTaskDetailTab('work');
                                        }
                                        if (group.key === 'attachments') {
                                          goToProjectTasks(item.project_id || item.task_project_id, item.workplace_id);
                                          openTaskFromSearch(item.task_id);
                                        }
                                        if (group.key === 'tags') {
                                          goToProjectTasks(selectedProject, selectedWorkspace);
                                          setTaskTagFilter(item);
                                          setTaskStatusFilter('all');
                                          setTaskPriorityFilter('all');
                                          setTaskAssigneeFilter('all');
                                        }
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {highlightMatch(
                                          item.title || item.name || item.filename || item.tag || item,
                                          searchText
                                        )}
                                      </Typography>
                                      {group.key === 'tasks' && (
                                        <Typography variant="caption" color="text.secondary">
                                          {item.status}
                                        </Typography>
                                      )}
                                    </Box>
                                  ))}
                                  {items.length > 5 && (
                                    <Typography
                                      variant="caption"
                                      color="primary"
                                      sx={{ cursor: 'pointer', mt: 0.5, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                      onClick={() => handleViewAllGroup(group.key)}
                                    >
                                      View all {group.label}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Paper>
                    )}
                  </Box>
                </Box>
      
                {/* RIGHT: Workspace select, New Task, mode toggle, profile */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexShrink: 0,
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="workspace-select-label">Workspace</InputLabel>
                    <Select
                      labelId="workspace-select-label"
                      value={selectedWorkspace || ''}
                      label="Workspace"
                      onChange={handleWorkspaceChange}
                    >
                      {workspaces.map((ws) => (
                        <MenuItem key={ws.id} value={ws.id}>
                          {ws.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
      
                  
      
                  
      
                {/* Profile / account */}
                  <IconButton size="small" onClick={handleOpenProfileMenu}>
                    <UserAvatar user={displayUserWithPhoto || { username: currentUserName }} size={28} />
                  </IconButton>
      
                </Box>
              </Toolbar>
            </AppBar>

      {/* PROFILE MENU */}
      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={handleCloseProfileMenu}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{
          elevation: 6,
          sx: {
            borderRadius: 3,
            p: 2.5,
            minWidth: 300,
            maxWidth: '100%',
            bgcolor: 'linear-gradient(135deg, #ecfdf3 0%, #e0f2fe 100%)',
          },
        }}
      >
        {/* Top: avatar + name details */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            mb: 2,
          }}
        >
          <UserAvatar user={displayUserWithPhoto || { username: currentUserName }} size={54} showName={false} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {profileFirstName || profileUserName || 'User'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
              <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Last name:
              </Typography>
              {profileLastName || 'Not provided'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
              <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                User name:
              </Typography>
              {profileUserName || 'Not provided'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
              <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Mail ID:
              </Typography>
              {profileEmail || (currentUserName?.includes('@') ? currentUserName : 'Not provided')}
            </Typography>
          </Box>
        </Box>

        {/* Last login line */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Last login details (feature yet to develop)
          </Typography>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Bottom row: Dark/Light, Settings, Invite, Logout */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          {/* Dark / Light */}
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => {
              onToggleMode && onToggleMode();
            }}
          >
            <IconButton size="small">
              <Brightness4Icon fontSize="small" />
            </IconButton>
            <Typography variant="caption">Dark / Light</Typography>
          </Box>

          {/* Settings (profile) */}
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => {
              handleCloseProfileMenu();
              setProfileOpen(true);
            }}
          >
            <IconButton size="small">
              <SettingsIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption">Settings</Typography>
          </Box>

          {/* Invite team */}
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => {
              handleCloseProfileMenu();
              setInviteDialogOpen(true);
            }}
          >
            <IconButton size="small">
              <GroupAddIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption">Invite team</Typography>
          </Box>

          {/* Logout */}
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => {
              handleCloseProfileMenu();
              setLogoutDialogOpen(true);
            }}
          >
            <IconButton size="small" color="error">
              <LogoutIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption">Logout</Typography>
          </Box>
        </Box>
      </Menu>


      {/* BODY: sidebar + main */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          
        }}
      >
       {/* SIDEBAR */}
               <Box
                 onMouseEnter={() => {
                   if (sidebarAutoCollapse) setSidebarHovering(true);
                 }}
                 onMouseLeave={() => {
                   if (sidebarAutoCollapse) setSidebarHovering(false);
                 }}
                 sx={{
                   position: 'fixed',
                   top: 56,                    // height of AppBar (Toolbar)
                   left: 0,
                   bottom: 0,
                   width: sidebarWidth,
                   transition: 'width 0.2s ease',
                   borderRight: '1px solid',
                   borderColor: 'divider',
                   bgcolor: 'background.paper',
                   display: 'flex',
                   flexDirection: 'column',
                   boxShadow: '2px 0 4px rgba(0,0,0,0.04)',
                 }}
               >
                 {/* Collapse/expand toggle */}
                 <Box
                   sx={{
                     position: 'absolute',
                     top: '50%',
                     right: -14,
                     transform: 'translateY(-50%)',
                     zIndex: 1300,
                   }}
                 >
                   <IconButton
                     size="small"
                     onClick={() =>
                       setSidebarAutoCollapse((prev) => !prev)
                     }
                     sx={{
                       bgcolor: 'background.paper',
                       borderRadius: '16px',
                       border: '1px solid',
                       borderColor: 'divider',
                       boxShadow: 1,
                       '&:hover': { bgcolor: 'background.default' },
                     }}
                   >
                     {sidebarAutoCollapse ? (
                       <KeyboardDoubleArrowRightIcon fontSize="small" />
                     ) : (
                       <KeyboardDoubleArrowLeftIcon fontSize="small" />
                     )}
                   </IconButton>
                 </Box>
       
                 
                 <Divider />
       
                 {/* sidebar content */}
                 {!isSidebarCollapsed && (
                 <Box
                   sx={{
                     flex: 1,
                     overflowY: 'auto',
                     px: 1.5,
                     py: 1.5,
                     display: 'flex',
                     flexDirection: 'column',
                     gap: 2,
                     scrollbarWidth: 'none', // hide scrollbar but keep scrollability
                     '&::-webkit-scrollbar': { display: 'none' },
                   }}
                 >
                     {/* Workspace select (sidebar) */}
                     
       
                     {/* Projects list */}
                     <Box>
                       <Box
                         sx={{
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'space-between',
                         }}
                       >
                         {showSidebarLabels && (
                           <Typography
                             variant="overline"
                             sx={{ fontSize: 11 }}
                             color="text.secondary"
                           >
                             Projects
                           </Typography>
                         )}
                         <Box
                           sx={{
                             display: 'flex',
                             alignItems: 'center',
                             gap: 0.5,
                           }}
                         >
                           <Tooltip title="New project" placement="right">
                             <IconButton
                               size="small"
                               onClick={() => setProjectDialogOpen(true)}
                             >
                               <AddCircleIcon fontSize="small" />
                             </IconButton>
                           </Tooltip>
                         </Box>
                       </Box>
       
                       {showSidebarLabels && (
                         <Box
                           sx={{
                             display: 'grid',
                             gridTemplateColumns: '1fr 1fr',
                             gap: 1,
                             mt: 1,
                             alignItems: 'center',
                           }}
                         >
                           <TextField
                             size="small"
                             placeholder="Search projects"
                             value={projectSearch}
                             onChange={(e) => setProjectSearch(e.target.value)}
                             fullWidth
                           />
                           <FormControl size="small" fullWidth>
                             <InputLabel>Jump</InputLabel>
                             <Select
                               label="Jump"
                               value={selectedProject || ''}
                               onChange={(e) => {
                                 const projId = e.target.value;
                                 setSelectedProject(String(projId));
                                 if (
                                   selectedWorkspaceObj &&
                                   selectedWorkspaceObj.name === 'Personal'
                                 ) {
                                   setCurrentView('project-tasks');
                                 } else {
                                   setCurrentView('project-overview');
                                 }
                               }}
                             >
                               {(filteredProjects || []).map((proj) => (
                                 <MenuItem key={proj.id} value={proj.id}>
                                   {proj.name}
                                 </MenuItem>
                               ))}
                             </Select>
                           </FormControl>
                         </Box>
                       )}
       
                       <Box
                         sx={{
                           mt: 0.5,
                           maxHeight: 140, // tightened to show fewer items before scrolling
                           overflowY: 'auto',
                           pr: showSidebarLabels ? 0.5 : 0,
                           scrollbarWidth: 'thin',
                           '&::-webkit-scrollbar': {
                             width: 6,
                           },
                           '&::-webkit-scrollbar-thumb': {
                             backgroundColor: 'rgba(0,0,0,0.2)',
                             borderRadius: 12,
                           },
                         }}
                       >
                         <List
                             dense
                             sx={{
                               '& .MuiListItemButton-root.Mui-selected': {
                                 bgcolor: 'action.selected',
                                 borderLeft: '3px solid',
                                 borderLeftColor: 'primary.main',
                               },
                               '& .MuiListItemButton-root.Mui-selected:hover': {
                                 bgcolor: 'action.selected',
                               },
                             }}
                           >
                           {(filteredProjects || []).map((proj) => (
                           <ListItemButton
                             key={proj.id}
                             selected={
                               String(proj.id) === String(selectedProject)
                             }
                             onClick={() => {
                               setSelectedProject(String(proj.id));
                               if (
                                 selectedWorkspaceObj &&
                                 selectedWorkspaceObj.name === 'Personal'
                                 ) {
                                   setCurrentView('project-tasks');
                                 } else {
                                   setCurrentView('project-overview');
                               }
                             }}
                             sx={{ borderRadius: 1, justifyContent: showSidebarLabels ? 'flex-start' : 'center' }}
                           >
                             <Tooltip title={proj.name} placement="right" disableHoverListener={showSidebarLabels}>
                               <ListItemIcon sx={{ minWidth: showSidebarLabels ? 36 : 0 }}>
                                 <ProjectAvatar name={proj.name} workspaceId={proj.workplace_id || selectedWorkspace} size={24} />
                               </ListItemIcon>
                             </Tooltip>
                             {showSidebarLabels && (
                               <ListItemText
                                 primaryTypographyProps={{
                                   fontSize: 13,
                                 }}
                                 primary={proj.name}
                                 />
                             )}
                             </ListItemButton>
                           ))}
                         </List>
                       </Box>
                     </Box>
       
                     {/* Small separator between Projects & My Views */}
                     <Box
                       sx={{
                         my: 0.5,
                         display: 'flex',
                         justifyContent: 'center',
                       }}
                     >
                       <Box
                         sx={{
                           width: showSidebarLabels ? '80%' : '50%',
                           borderBottom: '2px solid',
                           borderColor: 'divider',
                           opacity: 0.8,
                         }}
                       />
                     </Box>
       
       
                     {/* My views */}
                     <List
                       dense
                       sx={{
                         mt: 0.5,
                         '& .MuiListItemButton-root.Mui-selected': {
                           bgcolor: 'action.selected',
                           borderLeft: '3px solid',
                           borderLeftColor: 'primary.main',
                         },
                         '& .MuiListItemButton-root.Mui-selected:hover': {
                           bgcolor: 'action.selected',
                         },
                       }}
                     >
                       {/* Work Done */}
                       <ListItemButton
                         selected={currentView === 'workdone'}
                         onClick={() => setCurrentView('workdone')}
                         sx={{ borderRadius: 1, alignItems: 'flex-start', py: 0.75, justifyContent: showSidebarLabels ? 'flex-start' : 'center' }}
                       >
                         <Tooltip title="Work Done" placement="right" disableHoverListener={showSidebarLabels}>
                           <Box
                             sx={{
                               mr: showSidebarLabels ? 1 : 0,
                               mt: 0.3,
                               width: 28,
                               height: 28,
                               borderRadius: '50%',
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               bgcolor: 'rgba(25, 118, 210, 0.08)',
                             }}
                           >
                             <AccessTimeIcon sx={{ fontSize: 16 }} />
                           </Box>
                         </Tooltip>
                         {showSidebarLabels && (
                           <ListItemText
                             primary="Work Done"
                             primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                           />
                         )}
                       </ListItemButton>
       
                       {/* Team – yet to implement */}
                       <ListItemButton
                         sx={{ borderRadius: 1, alignItems: 'flex-start', py: 0.75, justifyContent: showSidebarLabels ? 'flex-start' : 'center' }}
                       >
                         <Tooltip title="Team" placement="right" disableHoverListener={showSidebarLabels}>
                           <Box
                             sx={{
                               mr: showSidebarLabels ? 1 : 0,
                               mt: 0.3,
                               width: 28,
                               height: 28,
                               borderRadius: '50%',
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               bgcolor: 'rgba(76, 175, 80, 0.08)',
                             }}
                           >
                             <GroupsIcon sx={{ fontSize: 16 }} />
                           </Box>
                         </Tooltip>
                         {showSidebarLabels && (
                           <ListItemText
                             primary="Team"
                             primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                           />
                         )}
                       </ListItemButton>
       
                       {/* Compliance – yet to implement */}
                       <ListItemButton
                         sx={{ borderRadius: 1, alignItems: 'flex-start', py: 0.75, justifyContent: showSidebarLabels ? 'flex-start' : 'center' }}
                       >
                         <Tooltip title="Compliance" placement="right" disableHoverListener={showSidebarLabels}>
                           <Box
                             sx={{
                               mr: showSidebarLabels ? 1 : 0,
                               mt: 0.3,
                               width: 28,
                               height: 28,
                               borderRadius: '50%',
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               bgcolor: 'rgba(255, 160, 0, 0.08)',
                             }}
                           >
                             <GavelIcon sx={{ fontSize: 16 }} />
                           </Box>
                         </Tooltip>
                         {showSidebarLabels && (
                           <ListItemText
                             primary="Compliance"
                             primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                           />
                         )}
                       </ListItemButton>
       
                       {/* Pending approvals – yet to implement */}
                       <ListItemButton
                         sx={{ borderRadius: 1, alignItems: 'flex-start', py: 0.75, justifyContent: showSidebarLabels ? 'flex-start' : 'center' }}
                       >
                         <Tooltip title="Pending approvals" placement="right" disableHoverListener={showSidebarLabels}>
                           <Box
                             sx={{
                               mr: showSidebarLabels ? 1 : 0,
                               mt: 0.3,
                               width: 28,
                               height: 28,
                               borderRadius: '50%',
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               bgcolor: 'rgba(244, 67, 54, 0.08)',
                             }}
                           >
                             <PendingActionsIcon sx={{ fontSize: 16 }} />
                           </Box>
                         </Tooltip>
                         {showSidebarLabels && (
                           <ListItemText
                             primary="Pending approvals"
                             primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                           />
                         )}
                       </ListItemButton>
       
                       {/* Client list – yet to implement */}
                       <ListItemButton
                         sx={{ borderRadius: 1, alignItems: 'flex-start', py: 0.75, justifyContent: showSidebarLabels ? 'flex-start' : 'center' }}
                       >
                         <Tooltip title="Client list" placement="right" disableHoverListener={showSidebarLabels}>
                           <Box
                             sx={{
                               mr: showSidebarLabels ? 1 : 0,
                               mt: 0.3,
                               width: 28,
                               height: 28,
                               borderRadius: '50%',
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               bgcolor: 'rgba(63, 81, 181, 0.08)',
                             }}
                           >
                             <ContactPageIcon sx={{ fontSize: 16 }} />
                           </Box>
                         </Tooltip>
                         {showSidebarLabels && (
                           <ListItemText
                             primary="Client list"
                             primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                           />
                         )}
                       </ListItemButton>
                     </List>
       
                   </Box>
                 )}
       
                 {/* Logout button */}
                 <Box sx={{
                         p: 1.5,
                         mt: 'auto',
                         borderTop: '1px solid',
                         borderColor: 'divider',
                       }}>
                     {showSidebarLabels ? (
                       <Button
                         fullWidth
                         variant="outlined"
                         color="inherit"
                         size="small"
                         startIcon={<LogoutIcon fontSize="small" />}
                         onClick={() => setLogoutDialogOpen(true)}
                       >
                         Logout
                       </Button>
                     ) : (
                       <Tooltip title="Logout" placement="right">
                         <IconButton
                           size="small"
                           color="inherit"
                           onClick={() => setLogoutDialogOpen(true)}
                           sx={{ mx: 'auto', display: 'flex' }}
                         >
                           <LogoutIcon fontSize="small" />
                         </IconButton>
                       </Tooltip>
                     )}
                   </Box>
       
                 {/* Logout dialog */}
                 <Dialog
                   open={logoutDialogOpen}
                   onClose={() => setLogoutDialogOpen(false)}
                 >
                   <DialogTitle>Confirm Logout</DialogTitle>
                   <DialogContent>
                     <Typography>
                       Are you sure you want to logout?
                     </Typography>
                   </DialogContent>
                   <DialogActions>
                     <Button
                       onClick={() => setLogoutDialogOpen(false)}
                     >
                       Cancel
                     </Button>
                     <Button
                       onClick={onLogout}
                       variant="contained"
                       color="error"
                     >
                       Logout
                     </Button>
                   </DialogActions>
                 </Dialog>
               </Box>
       

        {/* MAIN CONTENT */}
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            ml: `${sidebarWidth}px`,        // ✅ leave space for sidebar
            mt: '64px',                     // ✅ leave space for AppBar
            height: 'calc(100vh - 64px)',   // ✅ full height under AppBar
            pl: 3,
            pr: 0.5,
            pb: isAnyTasksView ? 1.5 : 3,
            bgcolor: 'background.default',
            overflow: 'hidden', // page stays fixed; inner content handles scrolling
            boxSizing: 'border-box',
          }}
        >
          <Box
            className="shell page-tasks"
            sx={{
              width: '100%',
              boxSizing: 'border-box',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                mt: isAnyTasksView ? 0.5 : 2,
                mb: isAnyTasksView ? 1 : 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
            <Box>
              {isHome && (
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600 }}
                  color="text.primary"
                >
                  {(() => {
                    const nameForGreeting =
                      profileFirstName || profileUserName || currentUserName || 'there';
                    const timePhrase = getTimeOfDay();
                    return `${timePhrase}, ${nameForGreeting}`;
                  })()}
                </Typography>
              )}
              {!isHome && selectedWorkspaceObj && !selectedProjectObj && (
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600 }}
                >
                  {selectedProjectObj}
                </Typography>
              )}
            </Box>

            
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
              }}
            >

          {/* PROJECT HEADER: name + tabs + settings */}
          {/* PROJECT HEADER: icon + name + settings + tabs */}
          {selectedProjectObj &&
            !isPersonalWorkspace &&
            ['project-overview', 'project-tasks'].includes(
              currentView
            ) && (
              <>
                <Box sx={{ mb: 1 }}>
                  {/* ROW 1: Project icon + name + settings (all together on left) */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ProjectAvatar
                        name={selectedProjectObj.name}
                        workspaceId={selectedProjectObj.workplace_id || selectedWorkspace}
                        size={32}
                      />
                    </Box>

                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {selectedProjectObj.name}
                    </Typography>

                    {/* ⚙️ sits right next to the name */}
                    {(isProjectOwner || isWorkspaceOwner) && (
                      <IconButton
                        size="small"
                        onClick={(e) => setProjectSettingsAnchor(e.currentTarget)}
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddCircleIcon />}
                      onClick = {handleNewTaskClick}
                      sx={{ ml: 'auto' }}
                      disabled={!canCreateTaskInCurrentContext}
                    >
                      New Task
                    </Button>
                  </Box>

                  {/* ROW 2: Project tabs (left) + view-mode tabs (right) */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      flexWrap: 'wrap',
                      
                    }}
                  >
                    {/* Project-level tabs */}
                    <Tabs
                      value={
                        ['project-overview', 'project-tasks'].includes(currentView)
                          ? currentView
                          : 'project-overview'
                      }
                      onChange={(_, value) => setCurrentView(value)}
                      sx={{ minHeight: 40 }}
                    >
                      <Tab label="Overview" value="project-overview" />
                      <Tab label="Tasks" value="project-tasks" />
                    </Tabs>

                    {/* View-mode tabs – right aligned */}
                    {currentView === 'project-tasks' && (
                      <Tabs
                        value={taskViewMode}
                        onChange={(_, v) => setViewMode(v)}
                        sx={{ ml: 'auto' }}
                      >
                        <Tab label="List" value="list" />
                        <Tab label="Board" value="board" />
                        <Tab label="Calendar" value="calendar" />
                      </Tabs>


                    )}
                  </Box>
                </Box>

                {/* Settings menu (unchanged) */}
                <Menu
                  anchorEl={projectSettingsAnchor}
                  open={projectSettingsOpen}
                  onClose={() => setProjectSettingsAnchor(null)}
                  keepMounted
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  PaperProps={{
                    elevation: 4,
                    sx: {
                      borderRadius: 2,
                      p: 1,
                      minWidth: 200,
                    },
                  }}
                >
                  {/* Edit project */}
                  <MenuItem
                    onClick={() => {
                      setProjectSettingsAnchor(null);
                      setEditingProject(selectedProjectObj);
                      setProjectDialogOpen(true);
                    }}
                  >
                    Edit project
                  </MenuItem>

                  {/* Task completion rules */}
                  <MenuItem
                    onClick={() => {
                      setProjectSettingsAnchor(null);
                      setCompletionRulesDialogOpen(true);
                    }}
                  >
                    Task completion rules
                  </MenuItem>

                  {/* Custom role */}
                  <MenuItem
                    onClick={() => {
                      setProjectSettingsAnchor(null);
                      setCustomRoleDialogOpen(true);
                    }}
                  >
                    Create custom role
                  </MenuItem>

                  {/* View audit logs */}
                  <MenuItem
                    onClick={async () => {
                      setProjectSettingsAnchor(null);
                      try {
                        const res = await api.get('/api/project-audit-logs', {
                          params: { project_id: selectedProject },
                        });
                        console.log('Project audit logs:', res.data);
                        enqueueSnackbar?.('Loaded audit logs in console', {
                          variant: 'info',
                        });
                      } catch (err) {
                        console.error('Error loading project logs', err);
                        enqueueSnackbar?.('Failed to load audit logs', {
                          variant: 'error',
                        });
                      }
                    }}
                  >
                    View audit logs
                  </MenuItem>

                  {/* Export audit logs */}
                  <MenuItem
                    onClick={async () => {
                      setProjectSettingsAnchor(null);
                      try {
                        const res = await api.get('/api/project-audit-logs', {
                          params: { project_id: selectedProject },
                        });
                        const blob = new Blob([JSON.stringify(res.data, null, 2)], {
                          type: 'application/json',
                        });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'project_audit_logs.json';
                        a.click();
                      } catch (err) {
                        console.error('Error exporting project logs', err);
                        enqueueSnackbar?.('Failed to export audit logs', {
                          variant: 'error',
                        });
                      }
                    }}
                  >
                    Export audit logs
                  </MenuItem>

                  {/* Archive project */}
                  <MenuItem
                    onClick={async () => {
                      setProjectSettingsAnchor(null);
                      try {
                        await api.put(`/api/projects/${selectedProject}/archive`, {
                          user_id: userId,
                        });
                        enqueueSnackbar?.('Project archived', {
                          variant: 'success',
                        });
                        if (selectedWorkspace) {
                          fetchProjects(selectedWorkspace);
                        }
                      } catch (err) {
                        console.error(
                          'Error archiving project',
                          err.response?.data || err.message
                        );
                        enqueueSnackbar?.('Failed to archive project', {
                          variant: 'error',
                        });
                      }
                    }}
                  >
                    Archive project
                  </MenuItem>
                </Menu>
              </>
            )}





          
          

          {/* Pending invites */}
          {(pendingInvites || []).length > 0 && (
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'warning.light',
                bgcolor: 'warning.light',
                color: 'warning.contrastText',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 600 }}
              >
                Workspace Invitations
              </Typography>
              {(pendingInvites || []).map((inv) => (
                <Box
                  key={inv.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box>
                    <Typography variant="body2">
                      You’ve been invited to{' '}
                      <strong>{inv.workspace_name}</strong> as{' '}
                      <strong>{inv.role}</strong>
                    </Typography>
                    <Typography variant="caption">
                      Invitation ID: {inv.id}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() =>
                        handleInviteAction(inv.id, 'accept')
                      }
                    >
                      Accept
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={() =>
                        handleInviteAction(inv.id, 'decline')
                      }
                    >
                      Decline
                    </Button>
                  </Box>
                </Box>
              ))}
            </Paper>
          )}

          <Grid container spacing={mainGridSpacing}>
            {/* ===== WORK DONE VIEW ===== */}
            {currentView === 'workdone' && (
              <>
                {/* filters & header */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                          Work Done
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Time logged against tasks. Each entry is
                          linked to a specific task.
                        </Typography>
                        {isWorkspaceOwner && selectedWorkspace && (
                          <Box sx={{ mt: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                setShowApprovalMatrix(
                                  (prev) => !prev
                                )
                              }
                            >
                              {showApprovalMatrix
                                ? 'Hide Approval Matrix'
                                : 'Show Approval Matrix'}
                            </Button>
                          </Box>
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1.5,
                        }}
                      >
                        <FormControl
                          size="small"
                          sx={{ minWidth: 120 }}
                        >
                          <InputLabel id="workdone-status-label">
                            Status
                          </InputLabel>
                          <Select
                            labelId="workdone-status-label"
                            label="Status"
                            value={workDoneStatusFilter}
                            onChange={(e) =>
                              setWorkDoneStatusFilter(
                                e.target.value
                              )
                            }
                          >
                            <MenuItem value="all">
                              All
                            </MenuItem>
                            <MenuItem value="pending">
                              Pending
                            </MenuItem>
                            <MenuItem value="approved">
                              Approved
                            </MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl
                          size="small"
                          sx={{ minWidth: 140 }}
                        >
                          <InputLabel id="workdone-view-label">
                            View
                          </InputLabel>
                          <Select
                            labelId="workdone-view-label"
                            label="View"
                            value={workDoneViewMode}
                            onChange={(e) =>
                              setWorkDoneViewMode(
                                e.target.value
                              )
                            }
                          >
                            <MenuItem value="mine">
                              My entries
                            </MenuItem>
                            <MenuItem value="team">
                              Team (if allowed)
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>

                    {/* Project filter checkboxes */}
                    {(projects || []).length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.5 }}
                        >
                          Projects
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={
                                  workDoneProjectFilterIds.length ===
                                  0
                                }
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setWorkDoneProjectFilterIds(
                                      []
                                    );
                                  }
                                }}
                              />
                            }
                            label="All"
                          />
                          {projects.map((p) => {
                            const checked =
                              workDoneProjectFilterIds.length ===
                                0 ||
                              workDoneProjectFilterIds.includes(
                                p.id
                              );
                            return (
                              <FormControlLabel
                                key={p.id}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={checked}
                                    onChange={(e) => {
                                      if (!e.target.checked) {
                                        setWorkDoneProjectFilterIds(
                                          (prev) =>
                                            prev.filter(
                                              (id) =>
                                                id !== p.id
                                            )
                                        );
                                      } else {
                                        setWorkDoneProjectFilterIds(
                                          (prev) => {
                                            if (
                                              prev.length === 0
                                            ) {
                                              return [p.id];
                                            }
                                            if (
                                              prev.includes(p.id)
                                            ) {
                                              return prev;
                                            }
                                            return [
                                              ...prev,
                                              p.id,
                                            ];
                                          }
                                        );
                                      }
                                    }}
                                  />
                                }
                                label={p.name}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                    {/* Date range buttons */}
                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        alignItems: 'center',
                      }}
                    >
                      {[
                        { key: 'today', label: 'Today' },
                        {
                          key: 'yesterday',
                          label: 'Yesterday',
                        },
                        { key: 'last7', label: 'Last 7 days' },
                        {
                          key: 'this_month',
                          label: 'This month',
                        },
                        {
                          key: 'last_month',
                          label: 'Last month',
                        },
                        { key: 'custom', label: 'Custom' },
                      ].map((opt) => (
                        <Button
                          key={opt.key}
                          size="small"
                          variant={
                            workDoneRangeKey === opt.key
                              ? 'contained'
                              : 'text'
                          }
                          onClick={() =>
                            setWorkDoneRangeKey(opt.key)
                          }
                        >
                          {opt.label}
                        </Button>
                      ))}
                      {workDoneRangeKey === 'custom' && (
                        <>
                          <TextField
                            type="date"
                            size="small"
                            label="From"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            value={workDoneCustomFrom}
                            onChange={(e) =>
                              setWorkDoneCustomFrom(
                                e.target.value
                              )
                            }
                          />
                          <TextField
                            type="date"
                            size="small"
                            label="To"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            value={workDoneCustomTo}
                            onChange={(e) =>
                              setWorkDoneCustomTo(
                                e.target.value
                              )
                            }
                          />
                        </>
                      )}
                    </Box>

                    {isWorkspaceOwner &&
                      selectedWorkspace &&
                      showApprovalMatrix && (
                        <Box sx={{ mt: 3 }}>
                          <ApprovalMatrix
                            workspaceId={
                              selectedWorkspace
                            }
                            currentUserId={userId}
                          />
                        </Box>
                      )}
                  </Paper>
                </Grid>

                {/* Hours by day */}
                <Grid item xs={12} md={4}>
                  <Fade in={true} timeout={600}>
                    <div>
                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          p: 2,
                          height: '100%',
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                          }}
                        >
                          Hours by day
                        </Typography>
                        <List dense>
                          {workDoneSummary.map((row) => {
                            const dateStr = row.work_date
                              ? String(
                                  row.work_date
                                ).slice(0, 10)
                              : '';
                            const totalMinutes = Number(
                              row.total_minutes || 0
                            );
                            const h = Math.floor(
                              totalMinutes / 60
                            );
                            const m = totalMinutes % 60;
                            const label = `${h}h ${m}m`;
                            const isSelected =
                              workDoneSelectedDate ===
                              dateStr;
                            return (
                              <ListItemButton
                                key={dateStr}
                                onClick={() =>
                                  setWorkDoneSelectedDate(
                                    isSelected
                                      ? ''
                                      : dateStr
                                  )
                                }
                                sx={{
                                  borderRadius: 1,
                                  mb: 0.5,
                                  bgcolor: isSelected
                                    ? 'action.selected'
                                    : 'transparent',
                                }}
                              >
                                <ListItemText
                                  primary={dateStr}
                                  secondary={label}
                                />
                              </ListItemButton>
                            );
                          })}
                          {(workDoneSummary || []).length ===
                            0 && (
                            <ListItem>
                              <ListItemText
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  color: 'text.secondary',
                                }}
                                primary="No work logged in this period."
                              />
                            </ListItem>
                          )}
                        </List>
                      </Paper>
                    </div>
                  </Fade>
                </Grid>

                {/* Work entries table */}
                <Grid item xs={12} md={8}>
                  <Fade in={true} timeout={600}>
                    <div>
                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          p: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                          }}
                        >
                          {workDoneSelectedDate
                            ? `Entries on ${workDoneSelectedDate}`
                            : 'Work done entries'}
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Task</TableCell>
                                <TableCell>Project</TableCell>
                                <TableCell align="right">
                                  Time
                                </TableCell>
                                <TableCell>
                                  Status
                                </TableCell>
                                <TableCell>User</TableCell>
                                <TableCell>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(workDoneSelectedDate
                                ? workDoneRows.filter(
                                    (row) =>
                                      row.work_date &&
                                      String(
                                        row.work_date
                                      ).slice(0, 10) ===
                                        workDoneSelectedDate
                                  )
                                : workDoneRows
                              ).map((row) => {
                                const dateStr = row.work_date
                                  ? String(
                                      row.work_date
                                    ).slice(0, 10)
                                  : '';
                                const totalMinutes =
                                  (row.hours != null
                                    ? Number(
                                        row.hours
                                      )
                                    : 0) *
                                    60 +
                                  (row.minutes != null
                                    ? Number(
                                        row.minutes
                                      )
                                    : 0);
                                const h = Math.floor(
                                  totalMinutes / 60
                                );
                                const m = totalMinutes % 60;
                                const timeLabel = `${h}h ${m}m`;
                                const status =
                                  row.status ||
                                  (row.approved
                                    ? 'approved'
                                    : 'pending');
                                const taskTitle =
                                  row.task_title ||
                                  row.task_name ||
                                  (row.task_id
                                    ? `Task #${row.task_id}`
                                    : '');
                                const projectName =
                                  row.project_name ||
                                  row.project ||
                                  '';
                                const userName =
                                  row.user_name ||
                                  row.username ||
                                  (row.user_id
                                    ? `User ${row.user_id}`
                                    : '');
                                const canApprove =
                                  workDoneViewMode ===
                                    'team' &&
                                  status !== 'approved' &&
                                  Number(row.user_id) !==
                                    Number(userId);
                                return (
                                  <TableRow
                                    key={row.id}
                                  >
                                    <TableCell>
                                      {dateStr}
                                    </TableCell>
                                    <TableCell>
                                      {taskTitle}
                                    </TableCell>
                                    <TableCell>
                                      {projectName}
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                    >
                                      {timeLabel}
                                    </TableCell>
                                    <TableCell>
                                      {status}
                                    </TableCell>
                                    <TableCell>
                                      {userName}
                                    </TableCell>
                                    <TableCell>
                                      {canApprove && (
                                        <Button
                                          size="small"
                                          variant="text"
                                          onClick={() =>
                                            handleApproveWorkLog(
                                              row.id
                                            )
                                          }
                                        >
                                          Approve
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                              {(workDoneRows || []).length ===
                                0 && (
                                <TableRow>
                                  <TableCell
                                    colSpan={7}
                                    align="center"
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ py: 2 }}
                                    >
                                      No work entries in
                                      this period.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </div>
                  </Fade>
                </Grid>
              </>
            )}

            {/* ===== HOME VIEW ===== */}
            {isHome && (
              <Grid size={{ xs: 12, md: 12 }}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Welcome to Krama
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    This is your home space – switch to a workspace/project to get started.
                  </Typography>
                </Paper>
              </Grid>
            )}

            

            {/* ===== WORKSPACE OVERVIEW (no project selected) ===== */}
            {isWorkspaceOverview && !selectedProjectObj && (
              <>
                <Grid item xs={12} md={8}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      {selectedWorkspaceObj.name} –
                      Overview
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Workspace overview will come here
                      (projects summary, workload,
                      goals, etc.).
                    </Typography>

                    <Tabs
                      value={workspaceTab}
                      onChange={(_, v) => setWorkspaceTab(v)}
                    >
                      <Tab label="Overview" />
                      <Tab label="Logs" />
                    </Tabs>

                    {workspaceTab === 0 && (
                      <Typography>Overview content</Typography>
                    )}

                    {workspaceTab === 1 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="h6">
                          Activity Logs
                        </Typography>
                        <Button
                          variant="outlined"
                          onClick={async () => {
                            const res = await api.get(
                              '/api/audit-reports',
                              {
                                params: {
                                  workspace_id:
                                    selectedWorkspace,
                                },
                              }
                            );
                            setWorkspaceLogs(res.data);
                          }}
                        >
                          Load Logs
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={async () => {
                            try {
                              const res = await api.get(
                                '/api/audit-reports',
                                {
                                  params: {
                                    workspace_id:
                                      selectedWorkspace,
                                    format: 'csv',
                                  },
                                  responseType: 'blob',
                                }
                              );
                              const blob = res.data;
                              const url =
                                window.URL.createObjectURL(
                                  blob
                                );
                              const a =
                                document.createElement('a');
                              a.href = url;
                              a.download = 'audit.csv';
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              window.URL.revokeObjectURL(
                                url
                              );
                            } catch (err) {
                              console.error(
                                'Error exporting audit CSV',
                                err
                              );
                              enqueueSnackbar(
                                'Failed to export CSV',
                                {
                                  variant: 'error',
                                }
                              );
                            }
                          }}
                        >
                          Export CSV
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={async () => {
                            try {
                              await api.get(
                                '/api/audit-reports',
                                {
                                  params: {
                                    workspace_id:
                                      selectedWorkspace,
                                    scheduled: true,
                                  },
                                }
                              );
                              enqueueSnackbar(
                                'Report scheduled',
                                {
                                  variant: 'success',
                                }
                              );
                            } catch (err) {
                              console.error(
                                'Error scheduling report',
                                err
                              );
                              enqueueSnackbar(
                                'Failed to schedule report',
                                {
                                  variant: 'error',
                                }
                              );
                            }
                          }}
                        >
                          Schedule Daily
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={async () => {
                            try {
                              const res = await api.get(
                                '/api/audit-reports',
                                {
                                  params: {
                                    workspace_id:
                                      selectedWorkspace,
                                    format: 'pdf',
                                  },
                                  responseType: 'blob',
                                }
                              );
                              const blob = res.data;
                              const url =
                                window.URL.createObjectURL(
                                  blob
                                );
                              const a =
                                document.createElement('a');
                              a.href = url;
                              a.download = 'audit.pdf';
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              window.URL.revokeObjectURL(
                                url
                              );
                            } catch (err) {
                              console.error(
                                'Error exporting audit PDF',
                                err
                              );
                              enqueueSnackbar(
                                'Failed to export PDF',
                                {
                                  variant: 'error',
                                }
                              );
                            }
                          }}
                        >
                          Export PDF
                        </Button>

                        <List>
                          {workspaceLogs.map((l) => (
                            <ListItem key={l.id}>
                              {l.message} - {l.created_at}
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    <Button
                      color="error"
                      onClick={() => {
                        api
                          .put(
                            `/api/workplaces/${selectedWorkspace}/archive`,
                            { user_id: userId }
                          )
                          .then(() =>
                            setArchiveOpen(true)
                          );
                      }}
                    >
                      Archive Workspace
                    </Button>

                    <TextField
                      label="Workspace Name"
                      value={
                        selectedWorkspaceObj?.name || ''
                      }
                      onChange={(e) => {
                        api
                          .put(
                            `/api/workplaces/${selectedWorkspace}/rename`,
                            {
                              name: e.target.value,
                              user_id: userId,
                            }
                          )
                          .then(() => {
                            enqueueSnackbar(
                              'Workspace renamed',
                              {
                                variant: 'success',
                              }
                            );
                            fetchWorkspaces();
                          })
                          .catch(() =>
                            enqueueSnackbar(
                              'Failed to rename',
                              {
                                variant: 'error',
                              }
                            )
                          );
                      }}
                      fullWidth
                      sx={{ mt: 2 }}
                      disabled={!isWorkspaceOwner}
                    />

                    {/* Logo upload + color */}
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      Upload Icon/Logo
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const formData =
                            new FormData();
                          formData.append(
                            'logo',
                            e.target.files[0]
                          );
                          formData.append(
                            'user_id',
                            userId
                          );
                          api
                            .put(
                              `/api/workplaces/${selectedWorkspace}/branding`,
                              formData
                            )
                            .then(() =>
                              enqueueSnackbar(
                                'Logo updated',
                                {
                                  variant: 'success',
                                }
                              )
                            );
                        }}
                      />
                    </Button>

                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel>Color</InputLabel>
                      <Select
                        label="Color"
                        value={
                          selectedWorkspaceObj?.color ||
                          '#000000'
                        }
                        onChange={(e) => {
                          api
                            .put(
                              `/api/workplaces/${selectedWorkspace}/branding`,
                              {
                                color: e.target.value,
                                user_id: userId,
                              }
                            )
                            .then(() =>
                              enqueueSnackbar(
                                'Color updated',
                                {
                                  variant: 'success',
                                }
                              )
                            );
                        }}
                      >
                        <MenuItem value="#000000">
                          Black
                        </MenuItem>
                        <MenuItem value="#FF0000">
                          Red
                        </MenuItem>
                        <MenuItem value="#00FF00">
                          Green
                        </MenuItem>
                      </Select>
                    </FormControl>

                    <Typography
                      variant="subtitle1"
                      sx={{ mt: 2 }}
                    >
                      Activity Logs
                    </Typography>

                    <Button
                      variant="outlined"
                      onClick={async () => {
                        try {
                          const res = await api.get(
                            '/api/audit-reports',
                            {
                              params: {
                                workspace_id:
                                  selectedWorkspace,
                              },
                            }
                          );
                          setWorkspaceLogs(
                            res.data || []
                          );
                        } catch (err) {
                          console.error(
                            'Error loading audit logs',
                            err
                          );
                          enqueueSnackbar(
                            'Failed to load logs',
                            {
                              variant: 'error',
                            }
                          );
                        }
                      }}
                    >
                      View Logs
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={async () => {
                        const res = await api.get(
                          '/api/activity-logs',
                          {
                            params: {
                              workplace_id:
                                selectedWorkspace,
                            },
                          }
                        );
                        const blob = new Blob(
                          [
                            JSON.stringify(
                              res.data,
                              null,
                              2
                            ),
                          ],
                          {
                            type: 'application/json',
                          }
                        );
                        const url =
                          URL.createObjectURL(blob);
                        const a =
                          document.createElement('a');
                        a.href = url;
                        a.download = 'logs.json';
                        a.click();
                      }}
                    >
                      Export Logs
                    </Button>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600 }}
                      >
                        Workspace Team
                      </Typography>
                      {isWorkspaceOwner &&
                        !isPersonalWorkspace && (
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1,
                            }}
                          >
                            <Button
                              size="small"
                              variant="text"
                              sx={{
                                textTransform: 'none',
                                fontSize: 11,
                              }}
                              onClick={() =>
                                setRoleManagerOpen(true)
                              }
                            >
                              Manage team
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{
                                textTransform: 'none',
                                fontSize: 11,
                              }}
                              onClick={() =>
                                setInviteDialogOpen(true)
                              }
                            >
                              Invite
                            </Button>
                          </Box>
                        )}
                    </Box>
                    <List dense>
                      {workspaceUsers.map((u) => (
                        <ListItem
                          key={u.id}
                          sx={{ px: 0 }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <UserAvatar user={u} size={28} />
                          </ListItemIcon>
                          <ListItemText
                            primaryTypographyProps={{
                              fontSize: 13,
                            }}
                            primary={buildUserDisplay(u).firstName}
                          />
                        </ListItem>
                      ))}
                      {(workspaceUsers || []).length ===
                        0 && (
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText
                            primaryTypographyProps={{
                              fontSize: 12,
                              color: 'text.secondary',
                            }}
                            primary="No members in this workspace yet."
                          />
                        </ListItem>
                      )}
                    </List>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1 }}
                      onClick={() =>
                        setPermissionGridOpen(true)
                      }
                    >
                      Update Team Roles
                    </Button>
                  </Paper>
                </Grid>
              </>
            )}

            {/* ===== PROJECT OVERVIEW ===== */}
            {isProjectOverview && selectedProjectObj && (
              <>
                <Grid item xs={12} md={8}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 2,
                    }}
                  >
                    {/* Title changed: no project name here now */}
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Project notes
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                       sx={{ mb: projectHeaderMb }}
                    >
                      Project notes from owner/admin – scope, context, links, etc.
                    </Typography>

                    <TextField
                      multiline
                      minRows={4}
                      maxRows={10}
                      fullWidth
                      placeholder="Write important notes for this project..."
                      value={projectNotesDraft}
                      onChange={(e) => setProjectNotesDraft(e.target.value)}
                    />

                    {/* Main actions: Save + Archive only */}
                    <Box
                      sx={{
                        mt: 1.5,
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveProjectNotes}
                        disabled={projectSaving}
                      >
                        {projectSaving ? 'Saving...' : 'Save Notes'}
                      </Button>

                     
                    </Box>

                    {/* Info text only – no extra buttons here */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Only project owner / workspace owner / project admins can edit notes.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                {/* Right side: Project team – unchanged */}
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600 }}
                      >
                        Project Team
                      </Typography>
                      {(isProjectOwner || isWorkspaceOwner) && (
                        <Button
                          size="small"
                          variant="text"
                          sx={{
                            textTransform: 'none',
                            fontSize: 11,
                          }}
                          onClick={() => setProjectRoleManagerOpen(true)}
                        >
                          Manage
                        </Button>
                      )}
                    </Box>

                    <List dense>
                      {projectUsers.map((u) => (
                        <ListItem key={u.id} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <UserAvatar user={u} size={28} />
                          </ListItemIcon>
                          <ListItemText
                            primaryTypographyProps={{
                              fontSize: 13,
                            }}
                            primary={buildUserDisplay(u).firstName}
                          />
                        </ListItem>
                      ))}

                      {(projectUsers || []).length === 0 && (
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText
                            primaryTypographyProps={{
                              fontSize: 12,
                              color: 'text.secondary',
                            }}
                            primary="No members in this project yet."
                          />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                </Grid>
              </>
            )}


            {/* ===== PROJECT TASKS VIEW ===== */}
            {isProjectTasks && !selectedProjectObj && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'divider',
                    p: 3,
                    textAlign: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Select a project to view tasks
                  </Typography>
                  <Typography variant="body2">
                    Choose a project from the sidebar or search, then click “View all” to load its task list.
                  </Typography>
                </Paper>
              </Grid>
            )}

            {isProjectTasks && selectedProjectObj && (
              <>
                <Grid item xs={12}>
                  <Fade in={true} timeout={600}>
                    <div>
                      {loadingTasks ? (
                        <Box
                          sx={{
                            textAlign: 'center',
                            py: 8,
                          }}
                        >
                          <CircularProgress size={20} />
                          <Typography
                            variant="body2"
                            sx={{ mt: 2 }}
                          >
                            Loading tasks...
                          </Typography>
                        </Box>
                          ) : (
                            <>
                              {taskViewMode === 'list' && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    mb: 0,
                                  }}
                                >
                                  

                                  

                                  
                                </Box>
                              )}
                              {taskViewMode === 'list' && (
                                <ProjectTasksTable
                                  tasks={filteredProjectTasks || []}
                                  onRowClick={openTaskPanel}
                                  getUserName={getUserName}
                                  userId={userId}
                                  assignableUsers={assignableUsers}
                                  setTasks={setTasks}
                                  taskStatusFilter={taskStatusFilter}
                                  setTaskStatusFilter={setTaskStatusFilter}
                                  taskPriorityFilter={taskPriorityFilter}
                                  setTaskPriorityFilter={setTaskPriorityFilter}
                                  taskAssigneeFilter={taskAssigneeFilter}
                                  setTaskAssigneeFilter={setTaskAssigneeFilter}
                                  taskTagFilter={taskTagFilter}
                                  setTaskTagFilter={setTaskTagFilter}
                                  taskDuePreset={taskDuePreset}
                                  setTaskDuePreset={setTaskDuePreset}
                                  taskTargetPreset={taskTargetPreset}
                                  setTaskTargetPreset={setTaskTargetPreset}
                                  taskCreatorFilter={taskCreatorFilter}
                                  setTaskCreatorFilter={setTaskCreatorFilter}
                                  taskDueCustomFrom={taskDueCustomFrom}
                                  taskDueCustomTo={taskDueCustomTo}
                                  setTaskDueCustomFrom={setTaskDueCustomFrom}
                                  setTaskDueCustomTo={setTaskDueCustomTo}
                                  taskTargetCustomFrom={taskTargetCustomFrom}
                                  taskTargetCustomTo={taskTargetCustomTo}
                                  setTaskTargetCustomFrom={setTaskTargetCustomFrom}
                                  setTaskTargetCustomTo={setTaskTargetCustomTo}
                                  availableTags={allProjectTags}
                                  showArchived={showArchivedTasks}
                                  onToggleArchived={setShowArchivedTasks}
                                  onClearFilters={() => {
                                    setTaskStatusFilter('all');
                                    setTaskPriorityFilter('all');
                                    setTaskAssigneeFilter('all');
                                  setTaskTagFilter('all');
                                  setTaskDateFilter(null);
                                setTaskDuePreset('all');
                                setTaskTargetPreset('all');
                                setTaskCreatorFilter('all');
                                setTaskDueCustomFrom('');
                                setTaskDueCustomTo('');
                                setTaskTargetCustomFrom('');
                                setTaskTargetCustomTo('');
                                setActiveTaskViewId(null);
                                setTaskQuickSearch('');
                              }}
                                  canApproveCompletions={canApproveCompletions}
                                  onApproveCompletion={handleApproveCompletion}
                                  savedTaskViews={savedTaskViews}
                                  activeTaskViewId={activeTaskViewId}
                                  savedViewLoading={savedViewLoading}
                                  onSelectTaskView={handleApplyTaskView}
                                  onSaveTaskView={handleSaveTaskView}
                                  onDeleteTaskView={handleDeleteTaskView}
                                  columnScopeKey={`${selectedWorkspace || 'ws'}:${selectedProject || 'none'}`}
                                />
                              )}

                          {taskViewMode === 'board' && (
                          <BoardView
                            tasks={filteredProjectTasks}
                            onUpdateStatus={async (id, status) => {
                              try {
                                await api.put(`/api/tasks/${id}`, {
                                  status,
                                  user_id: userId,
                                });

                                // Success – reload tasks
                                await fetchTasks();
                              } catch (err) {
                                console.error('Status update failed:', err?.response?.data || err.message);

                                const msg =
                                  err?.response?.data?.error ||
                                  'Unable to update task status. Please check completion rules.';

                                enqueueSnackbar(msg, { variant: 'error' });

                                // Re-sync board with backend state (in case UI moved the card)
                                fetchTasks();
                              }
                            }}
                          />
                        )}


                          {taskViewMode ===
                            'calendar' && (
                            <CalendarView
                              tasks={
                                filteredProjectTasks
                              }
                              onEventClick={
                                openTaskPanel
                              }
                              onNavigateToList={applyCalendarFilterToList}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </Fade>
                </Grid>
              </>
            )}

            {/* ===== PERSONAL WORKSPACE TASKS (no project selected) ===== */}
            {isPersonalTasksView && !selectedProjectObj && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'center',
                  }}
                >
                  <FormControl
                    size="small"
                    sx={{ minWidth: 140 }}
                  >
                    <InputLabel id="personal-filter-status-label">
                      Status
                    </InputLabel>
                    <Select
                      labelId="personal-filter-status-label"
                      label="Status"
                      value={taskStatusFilter}
                      onChange={(e) =>
                        setTaskStatusFilter(
                          e.target.value
                        )
                      }
                    >
                      <MenuItem value="all">
                        All
                      </MenuItem>
                      <MenuItem value="to-do">
                        To-Do
                      </MenuItem>
                        <MenuItem value="in-progress">
                          In-Progress
                        </MenuItem>
                        <MenuItem value="completed">
                          Completed
                        </MenuItem>
                        <MenuItem value="pending-approval">
                          Completed (awaiting approval)
                        </MenuItem>
                        <MenuItem value="closed">
                          Closed
                        </MenuItem>
                      </Select>
                    </FormControl>

                  <FormControl
                    size="small"
                    sx={{ minWidth: 140 }}
                  >
                    <InputLabel id="personal-filter-priority-label">
                      Priority
                    </InputLabel>
                    <Select
                      labelId="personal-filter-priority-label"
                      label="Priority"
                      value={taskPriorityFilter}
                      onChange={(e) =>
                        setTaskPriorityFilter(
                          e.target.value
                        )
                      }
                    >
                      <MenuItem value="all">
                        All
                      </MenuItem>
                      <MenuItem value="low">
                        Low
                      </MenuItem>
                      <MenuItem value="medium">
                        Medium
                      </MenuItem>
                      <MenuItem value="high">
                        High
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl
                    size="small"
                    sx={{ minWidth: 180 }}
                  >
                    <InputLabel id="personal-filter-assignee-label">
                      Assignee
                    </InputLabel>
                    <Select
                      labelId="personal-filter-assignee-label"
                      label="Assignee"
                      value={taskAssigneeFilter}
                      onChange={(e) =>
                        setTaskAssigneeFilter(
                          e.target.value
                        )
                      }
                    >
                      <MenuItem value="all">
                        All
                      </MenuItem>
                      <MenuItem value="unassigned">
                        Unassigned
                      </MenuItem>
                      {assignableUsers.map((u) => (
                        <MenuItem
                          key={u.id}
                          value={u.id}
                        >
                          {u.username}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    size="small"
                    sx={{ minWidth: 160 }}
                  >
                    <InputLabel id="personal-filter-tag-label">
                      Tag
                    </InputLabel>
                    <Select
                      labelId="personal-filter-tag-label"
                      label="Tag"
                      value={taskTagFilter}
                      onChange={(e) =>
                        setTaskTagFilter(
                          e.target.value
                        )
                      }
                    >
                      <MenuItem value="all">
                        All
                      </MenuItem>
                      {(allPersonalTags || []).map(
                        (tag) => (
                          <MenuItem
                            key={tag}
                            value={tag}
                          >
                            {tag}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>

                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      setTaskStatusFilter('all');
                      setTaskPriorityFilter('all');
                      setTaskAssigneeFilter('all');
                      setTaskTagFilter('all');
                    }}
                  >
                    Clear filters
                  </Button>

                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={showArchivedTasks}
                        onChange={(e) =>
                          setShowArchivedTasks(
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Show archived"
                  />
                </Box>

                <Fade in={true} timeout={600}>
                  <div>
                    <ProjectTasksTable
                      tasks={filteredPersonalTasks || []}
                      onRowClick={openTaskPanel}
                      getUserName={getUserName}
                      userId={userId}
                      assignableUsers={assignableUsers}
                      setTasks={setTasks}
                      handleModify={handleModify}
                    />
                  </div>
                </Fade>
              </Grid>
            )}

            {/* ===== FALLBACK PERSONAL TASKS LAYOUT ===== */}
            {!isHome &&
              !isWorkspaceOverview &&
              !isProjectOverview &&
              !isProjectTasks &&
              !isPersonalTasksView &&
              currentView !== 'workdone' && (
                <>
                  <Grid item xs={12} md={8}>
                    <Fade in={true} timeout={600}>
                      <div>
                        <TaskTable
                          title="Personally Created Tasks"
                          tasks={tasks}
                          onModify={handleModify}
                          userId={userId}
                          getUserName={getUserName}
                          assignableUsers={assignableUsers}
                        />
                      </div>
                    </Fade>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <SimpleListCard
                      title={teamListTitle}
                      items={teamList}
                      getLabel={(u) => u.username}
                    />
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Fade in={true} timeout={600}>
                      <div>
                        <TaskTable
                          title="Assigned Tasks"
                          tasks={assignedTasks}
                          onModify={handleModify}
                          userId={userId}
                          getUserName={getUserName}
                          assignableUsers={assignableUsers}
                        />
                      </div>
                    </Fade>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <SimpleListCard
                      title="Goals List"
                      items={goals || []}
                      getLabel={(g) => g.title}
                    />
                  </Grid>
                </>
              )}
          </Grid>
            </Box>
          </Box>
        </Box>
         </Box>

      

      {/* New Task dialog */}
      {/* New Task dialog – improved layout */}
      <Dialog
        open={taskDialogOpen}
        onClose={() => setTaskDialogOpen(false)}
        fullWidth
        maxWidth="md"   // feel free to change back to "sm" if you want it narrower
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                New Task
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Capture the basics, assign the right people, and set dates in one place.
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            pb: 2.5,
            '& .MuiFormControl-root, & .MuiTextField-root': {
              mt: 0,
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* SECTION 1 – BASICS */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                Task basics
              </Typography>

              <TextField
                autoFocus
                fullWidth
                sx={{ mt: 1.5 }}
                label="Title"
                placeholder="What needs to be done?"
                value={taskForm.title}
                onChange={(e) =>
                  setTaskForm((f) => ({ ...f, title: e.target.value }))
                }
              />

              <Box
                sx={{
                  mt: 2,
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' },
                }}
              >
                <FormControl size="small">
                  <InputLabel id="newtask-project-label">Project</InputLabel>
                  <Select
                    labelId="newtask-project-label"
                    label="Project"
                    value={taskForm.projectId}
                    onChange={(e) =>
                      setTaskForm((f) => ({ ...f, projectId: e.target.value }))
                    }
                  >
                    <MenuItem value="">
                      <em>Personal (no project)</em>
                    </MenuItem>
                    {visibleProjects.map((p) => (
                      <MenuItem key={p.id} value={String(p.id)}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small">
                  <InputLabel id="newtask-priority-label">Priority</InputLabel>
                  <Select
                    labelId="newtask-priority-label"
                    label="Priority"
                    value={taskForm.priority}
                    onChange={(e) =>
                      setTaskForm((f) => ({ ...f, priority: e.target.value }))
                    }
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ mt: 3, maxWidth: 220 }}>
                <InputLabel id="newtask-status-label">Status</InputLabel>
                <Select
                  labelId="newtask-status-label"
                  label="Status"
                  value={taskForm.status}
                  onChange={(e) =>
                    setTaskForm((f) => ({ ...f, status: e.target.value }))
                  }
                >
                  <MenuItem value="to-do">To-Do</MenuItem>
                  <MenuItem value="in-progress">In-Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
              </Box>
            </Box>

            {/* SECTION 2 – WHEN & WHO */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                When & who
              </Typography>

              <Box
                sx={{
                  mt: 1.5,
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                }}
              >
                <TextField
                  type="date"
                  size="small"
                  label="Target date"
                  InputLabelProps={{ shrink: true }}
                  value={taskForm.targetDate}
                  onChange={(e) =>
                    setTaskForm((f) => ({ ...f, targetDate: e.target.value }))
                  }
                />
                <TextField
                  type="date"
                  size="small"
                  label="Due date"
                  InputLabelProps={{ shrink: true }}
                  value={taskForm.dueDate}
                  onChange={(e) =>
                    setTaskForm((f) => ({ ...f, dueDate: e.target.value }))
                  }
                />
                <FormControl size="small">
                  <InputLabel id="newtask-assignee-label">Assign to</InputLabel>
                  <Select
                    labelId="newtask-assignee-label"
                    label="Assign to"
                    value={taskForm.assignedTo}
                    onChange={(e) =>
                      setTaskForm((f) => ({ ...f, assignedTo: e.target.value }))
                    }
                    renderValue={(value) => {
                      if (!value) return <Typography variant="body2">None</Typography>;
                      const u = assignableUsers.find((usr) => Number(usr.id) === Number(value));
                      const displayUser = u || { username: `User ${value}` };
                      return <UserAvatar user={displayUser} size={28} />;
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {assignableUsers.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <UserAvatar user={u} size={24} />
                          <Typography variant="body2">{buildUserDisplay(u).firstName}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              
            </Box>

            {/* SECTION 3 – PEOPLE & TAGS */}
            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              {selectedWorkspace && (
                <ProjectMultiTagSelector
                  projects={visibleProjects}
                  primaryProjectId={taskForm.projectId || ''}
                  value={linkedProjectIds}
                  onChange={setLinkedProjectIds}
                />
              )}

              <FormControl size="small" fullWidth>
                <InputLabel id="newtask-collab-label">Collaborators</InputLabel>
                <Select
                  labelId="newtask-collab-label"
                  label="Collaborators"
                  multiple
                  value={taskForm.collaborators}
                  onChange={(e) =>
                    setTaskForm((f) => ({
                      ...f,
                      collaborators: e.target.value,
                    }))
                  }
                  renderValue={(selected) => {
                    const ids = selected.map((id) => Number(id));
                    const users = assignableUsers.filter((u) => ids.includes(Number(u.id)));
                    if (!users.length) return 'None';
                    return (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {users.map((u) => (
                          <UserAvatar key={u.id} user={u} size={24} />
                        ))}
                      </Box>
                    );
                  }}
                >
                  {assignableUsers.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <UserAvatar user={u} size={24} />
                        <Typography variant="body2">{buildUserDisplay(u).firstName}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Tags (comma separated, e.g., bug, ops, finance)"
                size="small"
                fullWidth
                value={taskForm.tags}
                onChange={(e) =>
                  setTaskForm((f) => ({ ...f, tags: e.target.value }))
                }
              />
            </Box>


            {/* SECTION 4 – NOTES & ATTACHMENTS */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                Notes & attachments
              </Typography>

              <TextField
                label="Description / notes"
                multiline
                minRows={3}
                fullWidth
                sx={{ mt: 1.5 }}
                value={taskForm.description}
                onChange={(e) =>
                  setTaskForm((f) => ({ ...f, description: e.target.value }))
                }
              />

              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 0.5,
                  }}
                >
                  <Typography variant="subtitle2">Attachments</Typography>
                  <Button
                    variant="text"
                    size="small"
                    component="label"
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                  >
                    Add attachment
                    <input
                      type="file"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setTaskForm((f) => ({
                          ...f,
                          file,
                        }));
                        // allow re-selecting same file
                        e.target.value = '';
                      }}
                    />
                  </Button>
                </Box>

                <List dense sx={{ m: 0 }}>
                  {!taskForm.file && (
                    <ListItem sx={{ px: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        No attachments.
                      </Typography>
                    </ListItem>
                  )}

                  {taskForm.file && (
                    <ListItem
                      sx={{
                        px: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="body2">
                        {taskForm.file.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setTaskForm((f) => ({ ...f, file: null }))
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  )}
                </List>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateTask} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

 

      {/* Task detail side panel */}
      <TaskDetailPanel
        taskPanelOpen={taskPanelOpen}
        setTaskPanelOpen={setTaskPanelOpen}
        selectedTaskForPanel={selectedTaskForPanel}
        setSelectedTaskForPanel={setSelectedTaskForPanel}
        selectedTaskComments={selectedTaskComments}
        setSelectedTaskComments={setSelectedTaskComments}
        taskCommentDraft={taskCommentDraft}
        setTaskCommentDraft={setTaskCommentDraft}
        taskSteps={taskSteps}
        setTaskSteps={setTaskSteps}
        availableStepGroups={availableStepGroups}
        setAvailableStepGroups={setAvailableStepGroups}
        selectedStepGroupForAttach={selectedStepGroupForAttach}
        setSelectedStepGroupForAttach={setSelectedStepGroupForAttach}
        taskWorkLogs={taskWorkLogs}
        setTaskWorkLogs={setTaskWorkLogs}
        taskActivityLogs={taskActivityLogs}
        setTaskActivityLogs={setTaskActivityLogs}
        taskDetailTab={taskDetailTab}
        setTaskDetailTab={setTaskDetailTab}
        activityFrom={activityFrom}
        setActivityFrom={setActivityFrom}
        activityTo={activityTo}
        setActivityTo={setActivityTo}
        activityUserFilter={activityUserFilter}
        setActivityUserFilter={setActivityUserFilter}
        taskWorkLogDraft={taskWorkLogDraft}
        setTaskWorkLogDraft={setTaskWorkLogDraft}
        availableWorkLogApprovers={availableWorkLogApprovers}
        setAvailableWorkLogApprovers={setAvailableWorkLogApprovers}
        linkedProjectIds={linkedProjectIds}
        setLinkedProjectIds={setLinkedProjectIds}
        getUserName={getUserName}
        assignableUsers={assignableUsers}
        visibleProjects={visibleProjects}
        selectedWorkspace={selectedWorkspace}
        userId={userId}
        handleSaveTaskDetails={handleSaveTaskDetails}
        handleArchiveTask={handleArchiveTask}
        handleAddTaskComment={handleAddTaskComment}
        handleCollaboratorsChange={handleCollaboratorsChange}
        loadTaskComments={loadTaskComments}
        handleToggleStep={handleToggleStep}
        handleAttachStepGroup={handleAttachStepGroup}
        handleAddWorkLog={handleAddWorkLog}
        loadTask={loadTask}
        loadTaskActivityLogs={loadTaskActivityLogs}
        enqueueSnackbar={enqueueSnackbar}
        handleSaveProjectNotes={handleSaveProjectNotes}
        projectSaving={projectSaving}
        selectedProject={selectedProject}
        fetchTasks={fetchTasks}
        taskDetailSaving={taskDetailSaving}
        canLogWorkOnSelectedTask={canLogWorkOnSelectedTask}
        loadTaskWorkLogs={loadTaskWorkLogs} // From useSnackbar
        setTaskDetailSaving={setTaskDetailSaving}
      />

      {/* Bulk edit dialog */}
      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)}>
        <DialogTitle>Bulk Edit Tasks</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select
              value={bulkForm.status}
              onChange={(e) =>
                setBulkForm((f) => ({ ...f, status: e.target.value }))
              }
            >
              <MenuItem value="">No Change</MenuItem>
              <MenuItem value="to-do">To-Do</MenuItem>
              <MenuItem value="in-progress">In-Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel>Priority</InputLabel>
            <Select
              value={bulkForm.priority}
              onChange={(e) =>
                setBulkForm((f) => ({ ...f, priority: e.target.value }))
              }
            >
              <MenuItem value="">No Change</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel>Assignee</InputLabel>
            <Select
              value={bulkForm.assignee}
              onChange={(e) =>
                setBulkForm((f) => ({ ...f, assignee: e.target.value }))
              }
            >
              <MenuItem value="">No Change</MenuItem>
              <MenuItem value="unassign">Unassign</MenuItem>
              {assignableUsers.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.username}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Tags (comma separated)"
            margin="dense"
            value={bulkForm.tags}
            onChange={(e) =>
              setBulkForm((f) => ({ ...f, tags: e.target.value }))
            }
            helperText="Replace all tags"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              const updates = {};
              if (bulkForm.status) updates.status = bulkForm.status;
              if (bulkForm.priority) updates.priority = bulkForm.priority;
              if (bulkForm.assignee) {
                updates.assigned_to =
                  bulkForm.assignee === 'unassign'
                    ? null
                    : bulkForm.assignee;
              }
              if (bulkForm.tags) updates.tags = bulkForm.tags;

              try {
                const { data } = await api.put('/api/tasks/bulk', {
                  user_id: userId,
                  task_ids: selectedTasks,
                  updates,
                });

                const results = data?.results || [];
                const successCount = results.filter((r) => r.ok).length;
                const failCount = results.length - successCount;

                setBulkDialogOpen(false);
                setSelectedTasks([]);
                fetchTasks();

                if (failCount === 0) {
                  enqueueSnackbar(`Bulk update complete (${successCount} tasks)`, {
                    variant: 'success',
                  });
                } else {
                  enqueueSnackbar(
                    `Bulk update: ${successCount} updated, ${failCount} failed (check completion rules / permissions).`,
                    { variant: 'warning' }
                  );
                  // You can inspect exact errors in console if needed
                  console.log('Bulk update details:', results);
                }
              } catch (err) {
                console.error('Bulk update error:', err?.response?.data || err.message);
                enqueueSnackbar(
                  err?.response?.data?.error || 'Bulk update failed. Please try again.',
                  { variant: 'error' }
                );
              }
            }}
            variant="contained"
          >
            Apply
          </Button>

        </DialogActions>
      </Dialog>

      {/* Workspace Role Manager */}
      <RoleManager
        open={roleManagerOpen}
        onClose={() => setRoleManagerOpen(false)}
        workspaceId={selectedWorkspace}
        workspaceName={selectedWorkspaceObj?.name || ''}
        currentUserId={userId}
        workspaceOwnerId={selectedWorkspaceObj?.owner_id}
      />

      {/* Project Role Manager */}
      <ProjectRoleManager
        open={projectRoleManagerOpen}
        onClose={() => setProjectRoleManagerOpen(false)}
        projectId={selectedProject}
        projectName={selectedProjectObj?.name || ''}
        workspaceId={selectedWorkspace}
        currentUserId={userId}
        projectOwnerId={selectedProjectObj?.owner_id}
        workspaceOwnerId={selectedWorkspaceObj?.owner_id}
      />

      {/* Workspace Invite Dialog */}
      <WorkspaceInviteDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        workspaceId={selectedWorkspace}
        workspaceName={selectedWorkspaceObj?.name || ''}
        currentUserId={userId}
      />

      {/* Permission Grid Dialog */}
      <Dialog
        open={permissionGridOpen}
        onClose={() => setPermissionGridOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Manage Permissions</DialogTitle>
        <DialogContent>
          <PermissionGrid
            workspaceId={selectedWorkspace}
            users={workspaceUsers}
            customRoles={customRoles}
          />
        </DialogContent>
      </Dialog>

      {/* Archive Workspace Modal */}
      <ArchiveModal
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        workspaceId={selectedWorkspace}
      />

      {/* Project Dialog (Create / Edit) */}
      <ProjectDialog
        open={projectDialogOpen}
        onClose={() => setProjectDialogOpen(false)}
        workspaceId={selectedWorkspace}
        project={editingProject}
        currentUserId={userId}
        onSave={() => {
          fetchProjects(selectedWorkspace);
          if (editingProject) {
            setSelectedProject(editingProject.id); // Refresh selected
          }
        }}
      />

      {/* Custom Role Dialog */}
      <CustomRoleDialog
        open={customRoleDialogOpen}
        onClose={() => setCustomRoleDialogOpen(false)}
        onSave={() => fetchProjects(selectedWorkspace)}
        currentUserId={userId}
      />

      <ProjectCompletionRulesDialog
        open={completionRulesDialogOpen}
        onClose={() => setCompletionRulesDialogOpen(false)}
        project={selectedProjectObj}
        onSave={handleSaveCompletionRules}
      />


      {/* Profile Drawer / Dialog */}
      <Profile
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        userId={userId}
        isWorkspaceOwner={isWorkspaceOwner}
      />
    </Box>
  );
}

export default DashboardView;
